// Deterministic chat-command parsers for the Studio AI chat.
//
// The "only call AI when necessary" layer: these recognize simple, unambiguous
// requests (add a page / add a nav link / set a color / set a literal text value /
// show-hide) so the chat applies them INSTANTLY with no AI call. Every parser is
// conservative — it returns null unless it's confident, so anything fuzzy or
// generative (e.g. "make the heading more catchy") falls through to the AI path.

import type { Block, BlockType, NavLink } from '@realsite/shared-types';

export interface AddPageCommand {
  kind: 'add-page';
  name: string; // '' when the user said "add a page" without a name
}

export interface AddNavLinkCommand {
  kind: 'add-nav-link';
  label: string; // '' when no label could be extracted
  href?: string; // explicit path if the user gave one (e.g. "→ /about")
  target: 'nav' | 'footer'; // which chrome to add the link to (default nav)
  after?: string; // insert right AFTER this existing link ("under the word Agents")
  parent?: string; // NEST under this existing header link as a dropdown child ("as a
  //                   sub-item of Buyers", "in the Buyers dropdown", "under Buyers").
  //                   Header only — mutually exclusive with `after`.
}

export interface RemoveNavLinkCommand {
  kind: 'remove-nav-link';
  label: string; // the link text to find + remove (fuzzy: contains-match)
  target: 'nav' | 'footer' | 'any'; // where to look (default any)
}

export interface LogoResizeCommand {
  kind: 'logo-resize';
  /** bigger/smaller = step the current height; set = an absolute px value. */
  mode: 'bigger' | 'smaller' | 'set';
  /** present only when mode === 'set' */
  px?: number;
}

export type ColorTarget = 'background' | 'text';
export interface ColorEditCommand {
  kind: 'color';
  target: ColorTarget;
  value: string; // hex
}

export interface TextEditCommand {
  kind: 'text';
  field: string; // hint: heading | title | subheading | tagline | button
  value: string; // literal value to set
}

export interface VisibilityCommand {
  kind: 'visibility';
  hidden: boolean;
}

export type SimpleEdit = ColorEditCommand | TextEditCommand | VisibilityCommand;

// ── colors ────────────────────────────────────────────────────────────────────
const NAMED_COLORS: Record<string, string> = {
  // brand palette
  'midnight plum': '#10032A',
  plum: '#4E1F60',
  lavender: '#C9B8F4',
  'warm sand': '#E1CBA7',
  sand: '#E1CBA7',
  teal: '#6AA5A9',
  // common
  navy: '#1e3a5f',
  'sky blue': '#38bdf8',
  'light blue': '#93c5fd',
  blue: '#2563eb',
  crimson: '#b91c1c',
  maroon: '#7f1d1d',
  red: '#dc2626',
  emerald: '#10b981',
  'forest green': '#14532d',
  mint: '#a7f3d0',
  green: '#16a34a',
  gold: '#d4af37',
  amber: '#f59e0b',
  yellow: '#eab308',
  orange: '#ea580c',
  violet: '#8b5cf6',
  indigo: '#4f46e5',
  purple: '#7c3aed',
  magenta: '#c026d3',
  pink: '#ec4899',
  charcoal: '#1f2937',
  slate: '#334155',
  'light gray': '#d1d5db',
  'dark gray': '#374151',
  'light grey': '#d1d5db',
  'dark grey': '#374151',
  gray: '#6b7280',
  grey: '#6b7280',
  black: '#000000',
  white: '#ffffff',
  brown: '#92400e',
  tan: '#d2b48c',
  beige: '#f5f5dc',
  cream: '#fdf6e3',
  ivory: '#fffff0',
};
// Longest names first so "light blue" wins over "blue".
const COLOR_NAMES = Object.keys(NAMED_COLORS).sort((a, b) => b.length - a.length);

/** Words that mark a colour NAME as an actual colour instruction rather than a noun. */
const COLOR_CUE =
  /\b(colou?rs?|background|bg|text|font|theme|palette|scheme|paint(?:ed)?|shade|tone|tint|make|set|change|turn|update)\b/i;

/**
 * Extract a concrete color (hex or known name) from text, or null.
 *
 * `requireCue` makes a NAMED colour count only when a colour cue sits within a few words
 * of it. Colour words are also ordinary nouns and, more awkwardly, parts of names: the
 * brand "Simon Gray Realty" contains "gray", so "use the information in the brand manager
 * for the active simon gray realty brand" yielded #6b7280 and a section's background was
 * repainted grey. A hex code is unambiguous and always counts.
 *
 * Off by default because the site-scaffold parser legitimately reads bare colour words
 * out of a build prompt ("a navy and gold site"), where no cue is present or expected.
 * The EDIT paths pass it, because there the cost of a false positive is a visible,
 * unasked-for change to someone's live site.
 */
export function extractColor(text: string, opts?: { requireCue?: boolean }): string | null {
  const hex = text.match(/#[0-9a-f]{3}(?:[0-9a-f]{3})?\b/i);
  if (hex) return hex[0].toLowerCase();
  const lower = text.toLowerCase();
  for (const name of COLOR_NAMES) {
    const pattern = new RegExp(`\\b${name.replace(/\s+/g, '\\s+')}\\b`);
    const match = pattern.exec(lower);
    if (!match) continue;
    if (opts?.requireCue && !hasColorCueNear(lower, match.index, match[0].length)) continue;
    return NAMED_COLORS[name];
  }
  return null;
}

/** Is there a colour cue within four words either side of this match? */
function hasColorCueNear(lower: string, index: number, length: number): boolean {
  const before = lower.slice(0, index).split(/\s+/).slice(-4).join(' ');
  const after = lower.slice(index + length).split(/\s+/).slice(0, 5).join(' ');
  return COLOR_CUE.test(before) || COLOR_CUE.test(after);
}

/**
 * Tidy a chat prompt before any parser / the AI classifier sees it. People are chatty and
 * imprecise — they wrap the real instruction in greetings ("hey"), politeness ("can you please"),
 * hedges ("i was wondering if you could maybe just"), and trailing thanks. None of that changes
 * intent, but it throws off start-anchored regexes and muddies the model. We peel the filler off
 * the FRONT and BACK (looping, so stacked fillers all go) and normalize smart quotes/whitespace —
 * leaving the core command ("add a contact page") for the parsers to read. Never returns empty.
 */
export function normalizeChatPrompt(raw: string): string {
  // Armed build-interview sentinels (⟦gi:field=value⟧) are machine-generated and must pass through
  // VERBATIM — the value can be a URL (hero photo) whose case, "/", "?", or a substring like
  // "homepage" would otherwise be mangled by the trimming / synonym-folding below.
  if (/^\s*⟦gi:/.test(raw ?? '')) return (raw ?? '').trim();
  let s = (raw ?? '').replace(/ /g, ' ').replace(/[‘’ʼ`]/g, "'").replace(/[“”]/g, '"');
  s = s.replace(/\s+/g, ' ').trim();
  const original = s;
  let prev = '';
  while (s !== prev && s.length > 0) {
    prev = s;
    s = s
      // leading interjections / hedges
      .replace(/^(?:hey|hi|hello|yo|ok(?:ay)?|so|now|well|umm*|uhh*|alright|right|cool|great|thanks|sure)[\s,]+/i, '')
      // polite request wrappers: "can you (please/kindly/maybe/just)", "could u", "would ya"
      .replace(/^(?:can|could|would|will|can'?t)\s+(?:you|u|ya|we)\s+(?:please\s+|kindly\s+|maybe\s+|just\s+|also\s+)*/i, '')
      // "i want/need/wanna/would like (you) (to)" + the "i'd like" contraction (no space after i),
      // "i was wondering if you could", "i'm trying to"
      .replace(/^i\s*'?d\s+like\s+(?:you\s+)?(?:to\s+)?/i, '')
      .replace(/^i\s+(?:want|need|wanna|would\s+like)\s+(?:you\s+)?(?:to\s+)?/i, '')
      .replace(/^i\s+was\s+wondering\s+if\s+(?:you\s+)?(?:could|can|would)\s+(?:please\s+)?/i, '')
      .replace(/^i'?m\s+trying\s+to\s+/i, '')
      // bare leading softeners
      .replace(/^(?:please|pls|just|maybe|kindly|also|then|go\s+ahead\s+and|let'?s|lets|help\s+me(?:\s+to)?)\s+/i, '')
      // trailing politeness / hedges. NOTE the \b before the group: without it, the short "ty"
      // ("thanks") alternative also stripped the "ty" off any trailing word — "BLT Realty" → "BLT
      // Real", "Loyalty" → "Loyal". The boundary makes it match only a standalone " ty".
      .replace(/[\s,]*\b(?:please|pls|thanks(?:\s+so\s+much)?|thank\s+you|ty|for\s+me|for\s+us|if\s+(?:you\s+)?can|when\s+(?:you\s+)?can|asap)\s*[.!?]*$/i, '')
      // trailing sentence punctuation ("…under About?") so anchored regexes still match
      .replace(/\s*[?!.]+$/, '')
      .trim();
  }
  // Fold common informal synonyms to the canonical terms the parsers know — STRUCTURAL words
  // only (never content like "Photos"), so labels stay intact.
  s = s
    .replace(/\b(?:nav\s?bar|top\s+(?:nav|menu)|header\s+menu|main\s+menu|navigation\s+bar)\b/gi, 'nav menu')
    .replace(/\b(?:home\s?page|landing\s+page|front\s+page)\b/gi, 'home page')
    .replace(/\b(?:sub-?menu|sub\s+menu|fly-?out)\b/gi, 'dropdown')
    .replace(/\s+/g, ' ')
    .trim();
  // A desire-wrapper ("I need …") strips to a verbless noun phrase ("a page called X"), which the
  // add-parsers (gated on an ADD verb) then miss → it hit the paid AI. When what remains is an
  // INDEFINITE buildable noun with no leading action verb, canonicalize it to an explicit "add".
  if (
    /^(?:a|an|another)\s+.*\b(?:page|section|block|link|menu\s*item|nav\s*item|dropdown|form)\b/i.test(s) &&
    !/^(?:add|create|make|build|insert|put|place|include|new|need|want|remove|delete|hide|rename|change|edit|update|set|move|nest|show|turn|paint)\b/i.test(s)
  ) {
    s = `add ${s}`;
  }
  return s || original;
}

/**
 * Lenient label equality for matching user-typed names to existing links/pages despite typos and
 * casing/spacing/punctuation noise. Exact (normalized) first; else a small Levenshtein distance
 * scaled to length (so "contcat"≈"contact", "abuot"≈"about", but distinct names don't collide).
 */
export function fuzzyLabelEq(a: string, b: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
  const x = norm(a);
  const y = norm(b);
  if (!x || !y) return false;
  if (x === y || x.includes(y) || y.includes(x)) return true;
  const max = Math.max(x.length, y.length);
  if (max <= 3) return false; // too short to fuzzy safely
  // Levenshtein distance
  const d: number[] = Array.from({ length: y.length + 1 }, (_, i) => i);
  for (let i = 1; i <= x.length; i++) {
    let prev = d[0];
    d[0] = i;
    for (let j = 1; j <= y.length; j++) {
      const tmp = d[j];
      d[j] = Math.min(d[j] + 1, d[j - 1] + 1, prev + (x[i - 1] === y[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  const dist = d[y.length];
  // ≤1 edit for short words; ≤2 for 5+ chars (covers a transposition like "abuot"→"about",
  // which Levenshtein scores as 2). Nav/page labels are distinct enough not to collide at 2.
  return dist <= (max <= 4 ? 1 : 2);
}

/**
 * When the bot can't tell what a vague prompt means, ask a FOCUSED question instead of a generic
 * "I didn't get that". We read whatever the prompt hints at (menu? color? a page? an image?) and
 * steer the user toward a command the bot can act on — with concrete examples. Never blames them.
 */
export function clarifyResponse(prompt: string): string {
  const p = (prompt ?? '').toLowerCase();
  if (/\b(menu|nav|navbar|header|footer|link|dropdown|sub-?item)\b/.test(p))
    return 'I can change your menu — try “add a Contact link”, “remove the Blog link”, “rename Services to What We Do”, or “nest Join Us under About”. Which would you like?';
  if (/#[0-9a-f]{3,6}\b/i.test(p) || /\b(colou?rs?|navy|blue|teal|gold|green|red|black|white|palette|theme|background)\b/.test(p))
    return 'Which part should change color — the buttons, headings, links, or the background? e.g. “make the buttons navy”.';
  if (/\b(fonts?|typeface|typography)\b/.test(p))
    return 'For fonts, try “use Playfair for headings” or “set the body font to Inter”. Which font, and headings or body?';
  if (/\b(image|images|photo|photos|pic|pics|picture|logo|hero|banner)\b/.test(p))
    return 'For images, attach a photo and say “use this as the hero background”, or try “change the logo”. What image, and where should it go?';
  if (/\bpages?\b/.test(p))
    return 'Tell me the page action — “add a Contact page”, “rename About to Our Story”, or “edit the about page”.';
  if (/\b(text|headline|title|wording|copy|reword|rewrite|say)\b/.test(p))
    return 'Tell me what the text should say and where — e.g. “make the hero headline say ‘Find Your Dream Home’”.';
  return 'I can change text, colors, fonts, images, pages, and your menu. Try naming what to change — e.g. “make the hero say Welcome Home”, “change the buttons to navy”, “add a Contact page”, or “nest Join Us under About”.';
}

const ADD_VERB = /\b(add|create|make|build|new|insert|put|place|include|throw(?:\s+in)?|stick(?:\s+in)?|drop\s+in|set\s+up|give\s+me)\b/i;
// Page-level EDIT intent verbs — "edit/rewrite/change/update the about page".
// Distinct from ADD_VERB so the page planner can target an EXISTING page instead of
// creating one. (Named PAGE_EDIT_VERB to avoid colliding with the simple-edit
// EDIT_VERB used for color/text edits further below.)
const PAGE_EDIT_VERB = /\b(edit|rewrite|change|update|revise|redo|reword|rework|tweak|fix|improve)\b/i;

// ── whole-page generation (→ AI planner) ──────────────────────────────────────
/** True for "build/design/generate a [home]page/site [for/about/with X]" — a
 *  multi-section page generation request. Check this AFTER a named blank add-page
 *  ("add a page called About"), so a page literally named "About" isn't read as
 *  the "about" descriptor. Single-section "add a hero" stays an add-section. */
export function isPlanPageRequest(prompt: string): boolean {
  const p = prompt.toLowerCase();
  if (!/\b(build|design|generate|compose|create|make|lay\s?out|put\s+together)\b/.test(p)) return false;
  // "make the whole site MORE MODERN" is taste, not a brief — and routing it here would
  // REGENERATE the site over a request to restyle it. A comparative with no subject describes
  // how something should feel; a build says what it is about. "make a whole site for a
  // lakefront brokerage" still builds, because "for X" names the subject.
  const comparative = /\b(more|less|better|nicer|cleaner|prettier|fancier|simpler|bolder)\b/.test(p);
  const namesASubject = /\b(for|about|featuring|showcasing|targeting|aimed\s+at)\b/.test(p);
  if (comparative && !namesASubject) return false;
  const wholePage = /\b(home\s?page|landing\s?page|web\s?site|website|full\s+(?:page|site)|whole\s+(?:page|site))\b/.test(p);
  const pageWithDescriptor =
    /\bpage\b/.test(p) && /\b(for|about|with|that|featuring|including|to\s+(?:sell|help|attract|showcase|promote))\b/.test(p);
  return wholePage || pageWithDescriptor;
}

// ── rewrite a WHOLE page's copy ─────────────────────────────────────────────
/**
 * "rewrite the entire about page text to be about Simon Gray Realty and Dawn Pfaff".
 *
 * REPORTED, and the failure was quiet in the worst way: the request reached the agent, which
 * caps paid content tools at ONE per user message (`contentRuns >= 1` — a real guard against
 * surprise spend). So it edited a single block, answered "Updated the hero heading.", and said
 * nothing about the other sections. The user asked for a page and got one field, described as
 * a success.
 *
 * The cap is not the bug. Accepting a page-wide request as if one edit could satisfy it is. A
 * rewrite of N sections is N paid calls, so it has to be asked for explicitly and priced before
 * it runs — which is what `parsePageRewrite` exists to make possible.
 *
 * Deliberately narrow. It requires BOTH a rewrite verb and an explicit whole-page scope, so
 * "rewrite the about section" stays a single-block edit and only a genuinely page-wide phrasing
 * lands here.
 */
export interface PageRewriteCommand {
  /** What the page should now be about. Empty when they only said "rewrite this page". */
  brief: string;
  /** The page NAMED in the request ("the entire ABOUT page"), when one was. Undefined for
   *  "rewrite the whole page", which means whichever page is open.
   *
   *  Captured because the handler rewrites the page currently OPEN in the studio. Without it,
   *  saying "rewrite the entire about page" while sitting on Home rewrote Home — N sections,
   *  silently, on a page the user never mentioned. */
  page?: string;
}

const REWRITE_VERB = /\b(rewrite|redo|reword|rework|replace|update|change|refresh|revamp)\b/i;
/** The scope words that make this page-wide rather than section-wide. */
const WHOLE_PAGE_SCOPE =
  /\b(?:the\s+)?(?:entire|whole|full)\s+(?:\w+\s+)?page\b|\ball\s+(?:the\s+)?(?:text|copy|content|wording)\b|\bevery\s+section\b/i;

/**
 * "rewrite the about page to be about Dawn Pfaff" — page-scoped WITHOUT a scope word.
 *
 * Naming a PAGE as the object is itself the scope: "the about page" is the whole thing, as
 * opposed to "the about SECTION". Without this, the commonest phrasing there is fell through to
 * the agent, which cannot edit a page it does not consider open — so it asked the user to open
 * a page that WAS open, then asked what they wanted when they had already said twice.
 *
 * The lookahead is what keeps it page-scoped: "the about page" must END the object, or be
 * followed only by a brief connector. "rewrite the about page HEADING" is a field edit and must
 * not land here.
 */
const PAGE_AS_OBJECT =
  /\b(?:the|my|our|this)\s+(?:[a-z0-9][a-z0-9 &'-]*?\s+)?page\b(?=\s*[.!]?\s*$|\s+(?:to|so|about|around|with|using)\b)/i;

export function parsePageRewrite(prompt: string): PageRewriteCommand | null {
  const p = prompt.trim();
  if (!REWRITE_VERB.test(p) || !(WHOLE_PAGE_SCOPE.test(p) || PAGE_AS_OBJECT.test(p))) return null;
  // "add a section to the whole page" is not a rewrite; an ADD verb wins.
  if (/\b(add|insert|append|create)\b/i.test(p) && !REWRITE_VERB.test(p.split(/\b(?:add|insert|append|create)\b/i)[0] ?? '')) {
    return null;
  }
  // The brief is what follows the scope. Tried MOST SPECIFIC FIRST and never as one alternation:
  // a regex engine takes the leftmost match, so a bare `about` alternative matches the "about" in
  // "the entire ABOUT page" and captures "page text to be about Simon Gray Realty…" as the brief.
  const PATTERNS = [
    /\bto\s+be\s+about\s+(.+?)[\s.!]*$/i,
    /\bso\s+(?:it|they)(?:'s|\s+is|\s+are)?\s+about\s+(.+?)[\s.!]*$/i,
    /\bto\s+focus\s+on\s+(.+?)[\s.!]*$/i,
    /\bto\s+say\s+(.+?)[\s.!]*$/i,
  ];
  // Which page did they NAME? "the entire about page" → "about". Absent for "the whole page".
  const named =
    p.match(/\b(?:entire|whole|full)\s+([a-z0-9][a-z0-9 &'-]*?)\s+page\b/i) ??
    p.match(/\b(?:the|my|our|this)\s+([a-z0-9][a-z0-9 &'-]*?)\s+page\b(?=\s*[.!]?\s*$|\s+(?:to|so|about|around|with|using)\b)/i);
  // "this page" / "the page" name nothing — they mean whichever is open. And a capture that is
  // not a page name at all must be dropped, not carried: "all the text ON THIS page" captured
  // "text on this", which would then fail the wrong-page guard against a page nobody has.
  // isPageName already knows this shape — it is the same leak that made a section delete look
  // like a page delete.
  const rawPage = named ? cleanPageName(named[1]) : '';
  const page =
    rawPage && !/^(?:this|current|whole|entire|full)$/i.test(rawPage) && isPageName(rawPage)
      ? rawPage
      : undefined;

  for (const re of PATTERNS) {
    const m = p.match(re);
    if (m) return { brief: m[1].trim(), ...(page ? { page } : {}) };
  }
  // Bare "about X" last, and anchored to the LAST occurrence for the same reason.
  const bare = [...p.matchAll(/\babout\s+(.+?)[\s.!]*$/gi)].pop();
  // "about page" alone is the page's NAME, not a brief.
  const brief = bare ? bare[1].trim() : '';
  return { brief: /^page\b/i.test(brief) ? '' : brief, ...(page ? { page } : {}) };
}

/** The armed phrase the confirm chip sends. Reads naturally — it shows as the user's bubble. */
export function confirmPageRewritePrompt(brief: string): string {
  return brief ? `Yes, rewrite every section about ${brief}` : 'Yes, rewrite every section';
}

/** The armed confirmation → run it. */
export function parseConfirmedPageRewrite(prompt: string): PageRewriteCommand | null {
  const p = prompt.trim();
  const m = p.match(/^yes,?\s+rewrite every section(?:\s+about\s+(.+?))?[\s.!]*$/i);
  return m ? { brief: (m[1] ?? '').trim() } : null;
}

/**
 * "page" as the OBJECT OF A PREPOSITION — a target to put something on, not a thing to make.
 *
 * "add my headshot TO THE ABOUT PAGE" and "add a section TO THE ABOUT PAGE" are not page
 * creates, but both page parsers claimed them: one returned a create with an empty name, the
 * other a page-plan for "about" which — with no edit verb, against a page that has content —
 * would have created a DUPLICATE. Same shape as the one that published "New About".
 *
 * Two things defeated the older, narrower guard. The name between the determiner and "page" —
 * "to the page" was caught, "to the ABOUT page" was not. And the preposition list, which only
 * held the movement ones: "put the contact form AT THE BOTTOM OF the about page" walked past it
 * and became a page-plan for a page called "contact form at the bottom of the about", while
 * "add a hero at the top of the contact page" became a CREATE for "hero at the top of the
 * contact". Placement language is full of of/at/above/below, and all of it marks a target.
 */
/**
 * A preposition inside a captured page NAME means the capture ran past the name into a phrase.
 *
 * Same idea as PAGE_AS_TARGET, one level in: "a hero at the top of the contact page" captures
 * "hero at the top of the contact", which passed the older, shorter list (to/on/in/into/onto/
 * with/that/and) because it contains none of them — only "at" and "of". Kept as one list so the
 * two checks cannot drift apart again.
 */
const PREPOSITION_IN_NAME =
  /\b(?:to|on|in|into|onto|of|at|above|below|under|beneath|beside|within|with|that|and|featuring|including)\b/i;

const PAGE_AS_TARGET =
  /\b(?:to|on|in|into|onto|of|at|above|below|under|beneath|beside|within)\s+(?:(?:the|my|our|a|this|that|current)\s+)?(?:[a-z0-9][a-z0-9 &'-]*?\s+)?page\b/i;

// ── add a page ──────────────────────────────────────────────────────────────
/** "add a page (called X)" / "create an About page". Null if it's really an
 *  add-a-section-to-the-page request (that has a "to the page" target). */
export function parseAddPage(prompt: string): AddPageCommand | null {
  const p = prompt.trim();
  if (!ADD_VERB.test(p) || !/\bpage\b/i.test(p)) return null;
  const quoted = p.match(/["']([^"']+)["']/);
  const aXpage = p.match(/\b(?:a|an|another)\s+([a-z0-9][a-z0-9 &'-]*?)\s+page\b/i);
  // A genuine new-page name (reject a section-add phrase that drags a preposition into the
  // capture, e.g. "a contact form ON the page").
  const newPageName =
    aXpage && !PREPOSITION_IN_NAME.test(aXpage[1]) ? aXpage[1] : '';
  // "page called/named X" — an EXPLICIT name. Stop at a clause boundary so a trailing imperative
  // ("…, add a footer link") can't be swallowed. Computed up here so it ALSO suppresses the
  // section-add bail below: a trailing "…create content on that page" refers to the NEW page.
  const called = p.match(/\bpage\b\s*(?:called|named|titled|:)\s*["']?([^"'\n]+?)["']?(?:\s*[,;]|\s+(?:and|with|that)\b|$)/i);
  // "add a hero to the page" / "add basic terms on this page" — adding a section or
  // content to an EXISTING (usually the current) page, not creating a new one. When
  // "page" is the object of a preposition (to/on/in/into/onto), it's the TARGET, not
  // the thing being created — defer to the content/AI path. BUT an explicit name ("add a
  // [Name] page …", a quoted name, or "page called X") is a CREATE even when it then
  // refers to "this page" (fixes "make a page called Join Us and create content on that page"
  // falling through to Custom Design, which rebuilt the homepage).
  if (!newPageName && !quoted && !called && /\b(?:to|on|in|into|onto)\s+(?:the|my|our|a|this|that|current)?\s*page\b/i.test(p)) return null;

  let name = '';
  // "a page for/about X" with NO descriptor before "page" → X IS the name, e.g.
  // "make a page for terms and conditions" → "Terms and Conditions". (When there IS a
  // descriptor — "a privacy policy page for our brokerage" — aXpage wins above, so the
  // "for …" correctly stays context. cleanPageName trims any trailing imperative.)
  const pageForX = p.match(/\bpage\s+(?:for|about|on)\s+(.+)$/i);
  // Precedence: quoted > explicit "called X" > "a [Name] page" > "page for X". "called X" beats
  // the "a [Name] page" form so "make a NEW page called Resources" is named "Resources", not "new".
  if (quoted) name = quoted[1];
  else if (called) name = called[1];
  else if (newPageName) name = newPageName;
  else if (pageForX) name = pageForX[1];

  const cleaned = cleanPageName(name);
  // An EXPLICIT name — quoted or "called X" — is a create whatever else the sentence says:
  // "add a page called Join Us and put a form on that page" is still a create.
  const explicit = Boolean(quoted?.[1]?.trim() || called?.[1]?.trim());
  // Otherwise, a page that is only the TARGET of a preposition is not a page to make. Both
  // failure modes are here: "add my headshot to the about page" produced a create with an EMPTY
  // name, and "add a hero at the top of the contact page" produced one named "hero at the top
  // of the contact". The preposition decides, not whether the capture happened to be empty.
  if (!explicit && PAGE_AS_TARGET.test(p)) return null;
  return { kind: 'add-page', name: cleaned };
}

// ── add a nav / footer link ────────────────────────────────────────────────────
// Well-known destinations, so "add a link for the agent login / leads dashboard"
// resolves to the real route with a tidy label — instead of using the whole
// sentence as the label and a dead "#…" anchor. "Agent Login → /admin" is the
// established convention (the per-site admin / leads dashboard lives at /admin).
const KNOWN_NAV_DESTINATIONS: Array<{ test: RegExp; label: string; href: string }> = [
  { test: /\b(?:(?:agent|owner|staff|member|client)\s+)?(?:log\s?-?in|sign\s?-?in|login)\b/i, label: 'Agent Login', href: '/admin' },
  { test: /\b(?:leads|crm|admin|dashboard)\b/i, label: 'Agent Login', href: '/admin' },
];

/** If a phrase names a well-known destination (agent login / leads / admin), return
 *  its canonical { label, href }. Shared by the deterministic parser AND the AI
 *  intent fallback so both resolve to the SAME real route — and the model can never
 *  invent a path (e.g. "/agent-login") for a known destination. */
export function resolveKnownNavDestination(text: string): { label: string; href: string } | null {
  const hit = KNOWN_NAV_DESTINATIONS.find((d) => d.test.test(text));
  return hit ? { label: hit.label, href: hit.href } : null;
}

/** Trim a fuzzy phrase to a tidy nav/footer label: cut a trailing purpose/relative
 *  clause ("…to get to the leads"), drop articles + filler nouns, cap to 4 words,
 *  and Title-Case. So "agent login to get to the leads dashboard" → "Agent Login". */
export function cleanNavLabel(raw: string): string {
  let s = (raw ?? '').trim();
  // Cut at a purpose / relative clause ("X to get to Y", "X so I can Y", "X for the Y").
  s = s.split(/\s+(?:to|so|that|which|where|in\s+order|because|for\s+the)\b/i)[0] ?? '';
  s = s.replace(/^(?:the|a|an)\s+/i, '').replace(/[.,;:!?]+$/, '').trim();
  s = s.replace(/\s+(?:page|link|button)$/i, '').trim();
  const words = s.split(/\s+/).filter(Boolean).slice(0, 4);
  return words
    // Leave existing acronyms (FAQ, MLS) alone; Title-Case everything else.
    .map((w) => (/^[A-Z0-9&]{2,}$/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
}

/** "add a link to Contact" / "add a Listings link to the nav" / "link About in the
 *  footer" / "add a link for the agent login". Detects nav (default) vs footer, and
 *  resolves well-known destinations + a tidy label. */
/**
 * "make a page called X" is a request for a PAGE, whatever else the sentence says about menus.
 *
 * "make a page called join us and nest it under the about menu item" was claimed by the nav
 * parser — the word "nest" is enough to trigger it — and answered "Added 'Join Us And Nest' under
 * 'About' as a dropdown item → #join-us-and-nest". A menu entry pointing at an anchor. No page was
 * created, so renaming it found nothing and it never appeared in the page list. The agent asked
 * for a page and got a link to nowhere.
 *
 * Deliberately narrow: the add-verb has to govern the word "page" directly, so "add a link to the
 * About page" — a genuine nav request that happens to mention a page — is untouched.
 */
const MAKE_A_PAGE_RE =
  /\b(?:make|add|create|build|set\s+up|new)\s+(?:me\s+)?(?:a|an|another)?\s*(?:new\s+)?page\s+(?:called|named|titled|for)\b/i;

/**
 * The existing menu label a new page's link should nest under, from "…nest it under the About
 * menu item" / "…under About" / "…as a sub-item of Buyers".
 *
 * Reuses the shapes parseAddNavLink recognises, kept to the tail of the sentence so the name of
 * the page being created ("join us") is never mistaken for the parent.
 */
export function navParentFromPrompt(prompt: string): string | undefined {
  const p = prompt.trim();
  const m =
    // "under the About menu item" — the label PRECEDES the noun.
    p.match(/\bunder\s+(?:the\s+)?["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s+(?:menu|nav)[ -]?item\b/i) ??
    // "as a sub-item/child/dropdown of X", "nested under X"
    p.match(/\b(?:(?:as\s+)?(?:an?\s+)?(?:sub-?(?:item|link|menu|page)|child|drop-?down(?:\s+(?:item|link))?)\s+(?:of|under|in|to|within|beneath)|nested\s+(?:under|in|within|beneath))\s+(?:the\s+)?["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s*$/i) ??
    // "in the Buyers dropdown"
    p.match(/\bin(?:to)?\s+(?:the\s+)?["']?([a-z0-9][a-z0-9 &'-]*?)["']?(?:'s)?\s+(?:drop-?down|sub-?menu)\b/i) ??
    // bare "…under About" at the end — but not "under the word/link/item X" (a sibling position).
    p.match(/\b(?:nested\s+)?(?:under(?:neath)?|beneath)\s+(?!the\s+(?:word|link|item|nav\s+item|menu\s+item)\b)(?:the\s+)?["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s*$/i);
  if (!m) return undefined;
  // Drop a trailing descriptor noun so the parent is the link NAME ("About"), not "About menu".
  const label = m[1].replace(/\s+(?:menu|nav(?:igation)?|drop-?down|sub-?menu|item)$/i, '').trim();
  // A pronoun is not a label — "nest it under it" must not mint a menu item called "it".
  if (!label || /^(?:it|this|that|there|them)$/i.test(label)) return undefined;
  return label;
}

/**
 * A prompt that OPENS with a removal verb is a removal, whatever nav words follow.
 *
 * "remove the join us and nest menu under the about menu item" reached this parser and was
 * answered "“About” is already in your header menu." — the add path, reporting on the wrong item,
 * for a request to delete something. It got here because the nesting test matches the bare word
 * "nest", which in that sentence is part of the menu item's own (wrongly created) NAME, not an
 * instruction. ADD_VERB never matched at all.
 *
 * Anchored to the start, and `drop` is deliberately absent: ADD_VERB owns "drop in a link".
 */
const LEADING_REMOVE_RE =
  /^\s*(?:please\s+|pls\s+|can\s+you\s+|could\s+you\s+|would\s+you\s+|hey\s+)*(?:remove|delete|get\s+rid\s+of|take\s+off|take\s+out|unnest)\b/i;

export function parseAddNavLink(prompt: string): AddNavLinkCommand | null {
  const p = prompt.trim();
  // A page request goes to the page path, which creates the page AND can menu it. Claiming it
  // here produced a dropdown item and nothing to click through to.
  if (MAKE_A_PAGE_RE.test(p)) return null;
  // A removal is never an add. Let parseRemoveNavLink (or the planner) have it.
  if (LEADING_REMOVE_RE.test(p)) return null;
  // Fire on "link" OR a menu-item/dropdown/"nest" phrasing ("add a menu item …",
  // "nest a sub-item …"), with an add-verb (or a leading "link …" / "nest …").
  const MENU_NOUN = /\b(?:link|menu\s*item|nav\s*item|sub-?(?:item|link|menu|page)|drop-?down|menu\s+entry)\b/i;
  const NEST_INTENT = /\bnest(?:ed|ing)?\b|\bsub-?(?:item|link|menu|page)\b|\bchild\b|\bdrop-?down\b/i.test(p);
  if (!MENU_NOUN.test(p) && !NEST_INTENT) return null;
  // Needs an add-verb, a leading "link …"/"nest …", or — for a clear nesting request — a
  // move-style verb (put/place/move/drop) so "put X in the Y dropdown" works too.
  if (!ADD_VERB.test(p) && !/^\s*(?:link|nest)\b/i.test(p) && !(NEST_INTENT && /\b(?:put|place|move|drop|nest)\b/i.test(p))) return null;

  const target: 'nav' | 'footer' = /\bfooter\b/i.test(p) ? 'footer' : 'nav';

  // NESTING (header only): make the link a dropdown CHILD of an existing link — distinct
  // from `after` (sibling order). "as a sub-item/child/dropdown of X", "nested under X",
  // "in the X dropdown/sub-menu", or a bare "under X" — but NOT "under the word/link/item
  // X" (that's a sibling position) and NOT a reserved chrome word (nav/header/footer/…).
  const NEST_OF_RE = /\b(?:(?:as\s+)?(?:an?\s+)?(?:sub-?(?:item|link|menu|page)|child|drop-?down(?:\s+(?:item|link))?)\s+(?:of|under|in|to|within|beneath)|nested\s+(?:under|in|within|beneath))\s+(?:the\s+)?["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s*$/i;
  const IN_DROPDOWN_RE = /\bin(?:to)?\s+(?:the\s+)?(?!nav(?:igation)?\b|header\b|footer\b|main\b|top\b)["']?([a-z0-9][a-z0-9 &'-]*?)["']?(?:'s)?\s+(?:drop-?down|sub-?menu)\b/i;
  const BARE_UNDER_RE = /\b(?:nested\s+)?(?:under(?:neath)?|beneath)\s+(?!the\s+(?:word|link|item|nav\s+item|menu\s+item)\b)(?:the\s+)?["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s*$/i;
  // "under the About menu item" / "under the About nav item" → nest under "About" (the name
  // PRECEDES the noun). Distinct from positional "under the word/menu item X" (name FOLLOWS).
  const UNDER_X_ITEM_RE = /\bunder\s+(?:the\s+)?["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s+(?:menu|nav)[ -]?item\b/i;
  let parent: string | undefined;
  if (target === 'nav') {
    const pm = p.match(NEST_OF_RE) ?? p.match(IN_DROPDOWN_RE) ?? p.match(UNDER_X_ITEM_RE) ?? p.match(BARE_UNDER_RE);
    if (pm) parent = cleanNavLabel(pm[1]) || undefined;
    // Explicit nesting intent ("nest", "sub-item", "dropdown") also reads the positional
    // phrasings as the PARENT ("under the menu item X", "under X").
    if (!parent && NEST_INTENT) {
      const nm =
        p.match(/\bunder\s+(?:the\s+)?(?:menu|nav)[ -]?item\s+["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s*$/i) ??
        p.match(/\b(?:under(?:neath)?|beneath|within)\s+(?:the\s+)?["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s*$/i);
      if (nm) parent = cleanNavLabel(nm[1]) || undefined;
    }
    // "under the About menu" / "…dropdown" / "…nav item" — drop a trailing descriptor noun so
    // the parent is the link NAME ("About"), not "About menu" (which would match no link).
    if (parent) parent = parent.replace(/\s+(?:menu|nav(?:igation)?|drop-?down|sub-?menu|item)$/i, '').trim() || parent;
  }

  // Repositioning an EXISTING link ("put the Contact link BEFORE / ahead of About") is a REORDER,
  // not an add — and there's no deterministic before-slot (only `after`). Defer to the agent so it
  // moves the real link, instead of minting a junk "Before About" item (leftover positional text
  // becoming the label). A nesting request keeps its own path.
  if (!parent && /\bthe\s+[a-z0-9][a-z0-9 &'-]*?\s+link\b/i.test(p) && /\b(?:before|ahead\s+of|in\s+front\s+of)\b/i.test(p)) {
    return null;
  }

  // Positional placement: "…under/after/below the word Agents" → insert right after the
  // existing "Agents" link. Skipped when this is a nesting request (parent wins).
  const POSITION_RE = /\b(?:under(?:neath)?|after|below|beneath|beside|next\s+to)\s+(?:the\s+)?(?:word\s+|link\s+|item\s+|nav\s+item\s+|menu\s+item\s+)?["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s*$/i;
  const afterM = parent ? null : p.match(POSITION_RE);
  const after = afterM ? cleanNavLabel(afterM[1]) || undefined : undefined;

  // An explicit path the user typed ("… → /about") always wins.
  let href: string | undefined;
  const hrefM = p.match(/(?:->|→|to|:)\s*(\/[a-z0-9/_-]+)\b/i);
  if (hrefM) href = hrefM[1];

  // Known destination (agent login / leads / admin / dashboard) → tidy label + real
  // route, unless the user already gave an explicit path.
  const dest = resolveKnownNavDestination(p);
  if (dest) return { kind: 'add-nav-link', label: dest.label, href: href ?? dest.href, target, after, parent };

  const base = p
    // drop a trailing explicit path ("… → /about")
    .replace(/\s*(?:->|→|to|:)\s*\/[a-z0-9/_-]+\s*$/i, '')
    // strip the target phrase WHEREVER it appears — "…in the footer for X" must keep
    // "for X" (the old trailing `.*$` ate the real label).
    .replace(/\s+(?:to|in|on)\s+(?:the\s+)?(?:nav(?:igation)?|menu|header|footer)\b/gi, ' ')
    // strip a trailing positional phrase ("…under the word Agents") — captured as `after`.
    .replace(POSITION_RE, '')
    // strip a nesting phrase ("…in the Buyers dropdown", "…as a sub-item of Buyers") so
    // the parent name isn't mistaken for the link label.
    .replace(IN_DROPDOWN_RE, ' ')
    .replace(NEST_OF_RE, ' ')
    // strip a leftover bare nesting verb ("…and nest it", "…nest this") — the parent is captured
    // separately, so without this the label swallowed it: "add a link to Join Us and nest it under
    // About" → label "Join Us And Nest" (+ dead #anchor) instead of "Join Us". Requires "and" or a
    // pronoun so a real label containing "nest" (e.g. "Bird Nest") isn't clipped.
    .replace(/\s+(?:and\s+nest(?:ed|ing)?(?:\s+(?:it|this|that))?|nest(?:ed|ing)?\s+(?:it|this|that))\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  let raw = '';
  const toLabel = base.match(
    /\blink\b\s*(?:to|for|called|named|labell?ed)?\s+(?:the\s+)?["']?([^"'\n/]+?)["']?(?:\s+(?:page|link))?\s*$/i,
  );
  const aXlink = base.match(/\b(?:a|an)\s+["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s+link\b/i);
  // Prefer "a [X] link" (X is the label) over "link X": "a privacy policy link under
  // the word Agents" → label "privacy policy", not the trailing positional phrase.
  // "link X" / "a link to X" (no "a [X] link") still fall to toLabel.
  // "…called/named 'X'" is the most explicit label; then "(menu) item X"; then the link forms.
  const calledM = p.match(/\b(?:called|named|labell?ed|titled)\s+["']?([a-z0-9][a-z0-9 &'-]*?)["']?(?:\s+(?:to|in|on|under|as|within|beneath)\b|\s*$)/i);
  // "menu item X" (name FOLLOWS the noun). Negative lookahead so a trailing nest/position
  // clause isn't mis-read as the label — "the agents menu item UNDER about" must NOT capture
  // "under about" (the reported "Under About" bug).
  const itemM = p.match(/\b(?:menu|nav)\s*item\s+(?:called\s+|named\s+|labell?ed\s+)?(?!(?:under|underneath|beneath|in|into|on|to|as|within|of|after|below)\b)["']?([a-z0-9][a-z0-9 &'-]*?)["']?(?:\s+(?:to|in|on|under|as|within|beneath)\b|\s*$)/i);
  // "the X menu item" / "the X nav item" — X is an EXISTING item's name that PRECEDES the
  // descriptor (distinct from itemM's name-follows form). Powers re-parent phrasings:
  // "nest the agents menu item under about" → move the existing "Agents" item. Guarded to
  // nesting requests (a parent was detected) and to non-article names.
  const existingItemM = p.match(/\b(?:the|a|an|this|that|my|our)?\s*["']?([a-z0-9][a-z0-9'&-]*(?:\s+[a-z0-9][a-z0-9'&-]*)?)["']?\s+(?:menu|nav)[ -]?item\b/i);
  if (calledM) raw = calledM[1];
  else if (itemM) raw = itemM[1];
  else if (parent && existingItemM && !/^(?:a|an|the)$/i.test(existingItemM[1].trim())) raw = existingItemM[1];
  else if (aXlink) raw = aXlink[1];
  else if (toLabel) raw = toLabel[1];
  // Bare nesting phrasing with no "called/link/menu item" marker — "nest Join Us under About",
  // "make Join Us a submenu of About", "put Join Us in the About dropdown". The label is the text
  // between the verb and the nest/parent clause. Only when a parent was detected (avoids grabbing
  // a label off non-nesting commands).
  else if (parent) {
    const m = p.match(
      /\b(?:nest|add|create|make|put|place|insert|move)\s+(?:a\s+|an\s+|the\s+)?(?:new\s+)?(?:link|menu\s*item|nav\s*item|sub-?item|sub-?menu|dropdown(?:\s+item)?|item)?\s*["']?([a-z0-9][a-z0-9 &'-]*?)["']?\s+(?:to\s+(?:be\s+)?(?:nest(?:ed)?\s+)?)?(?:(?:as\s+)?(?:a\s+|an\s+)?(?:sub-?(?:item|menu|link)|dropdown|child)\s+)?(?:of|under(?:neath)?|beneath|within|in(?:to)?|nested)\b/i,
    );
    if (m) raw = m[1];
  }
  raw = raw.trim();
  // "link it/this/that in the footer" — a pronoun isn't a real label.
  if (/^(it|this|that|the page)$/i.test(raw)) raw = '';
  return { kind: 'add-nav-link', label: cleanNavLabel(raw), href, target, after, parent };
}

// ── remove a nav / footer link ──────────────────────────────────────────────────
// "take out/off" also matches the SEPARABLE phrasal verb ("take the Contact link OUT of the
// footer") where the object sits between "take" and the particle — the gate only needs presence.
const REMOVE_VERB = /\b(?:remove|delete|drop|get\s+rid\s+of)\b|\btake\b[^.?!]*\b(?:out|off)\b/i;

/** "remove the agent login link from the header" / "delete 'X' from the footer" /
 *  "take the blog link out of the menu". Returns the link TEXT to remove (matched
 *  fuzzily / contains downstream) + where to look (nav | footer | any). */
export function parseRemoveNavLink(prompt: string): RemoveNavLinkCommand | null {
  const p = prompt.trim();
  if (!REMOVE_VERB.test(p)) return null;
  // Must be about a nav/menu/header/footer LINK (so "remove the hero" stays a block op).
  if (!/\b(nav(?:igation)?|menu|header|footer|link)\b/i.test(p)) return null;

  const target: 'nav' | 'footer' | 'any' = /\bfooter\b/i.test(p)
    ? 'footer'
    : /\b(?:nav(?:igation)?|menu|header)\b/i.test(p)
      ? 'nav'
      : 'any';

  let label = '';
  // Match a double- or smart-quoted string FIRST so an apostrophe inside it doesn't
  // truncate the label (the "Let's Talk" → "Let" bug). Single quotes only as a last
  // resort (and they'd hit the apostrophe too, but most labels use double quotes).
  const quoted = p.match(/"([^"]+)"/) || p.match(/[“”]([^“”]+)[“”]/) || p.match(/'([^']+)'/);
  // "the menu item called/named About" — the name FOLLOWS the noun. The positional
  // fallbacks below capture everything after the verb, so this phrasing used to produce
  // the label "menu item called About", which matches no real link and dead-ends with
  // "couldn't find that link" — reported from real use as the chat "confusing" the request.
  const called = p.match(/\b(?:called|named|titled)\s+["'“]?(.+?)["'”]?\s*[.!]?\s*$/i);
  if (quoted) {
    label = quoted[1];
  } else if (called) {
    label = called[1];
  } else {
    const m =
      p.match(/\b(?:remove|delete|drop|take|get\s+rid\s+of)\s+(?:out\s+|off\s+)?(?:the\s+)?(.+?)\s+(?:link\s+|item\s+)?(?:from|out\s+of|in)\b/i) ||
      p.match(/\b(?:remove|delete|drop|get\s+rid\s+of)\s+(?:the\s+)?(.+?)(?:\s+link|\s+item)?\s*$/i);
    if (m) label = m[1];
  }
  label = label
    .replace(/^(?:the|a|an)\s+/i, '')
    // "remove the PAGE join us from the menu" — the noun rode into the capture ahead of
    // the name; the delete-page parser now steps aside for from-the-menu phrasings, so
    // this label must come out clean here.
    .replace(/^page\s+/i, '')
    // Drop a trailing clause that LOCATES the item rather than naming it: "…under the About menu
    // item", "…in the Buyers dropdown", "…from the header". Without this, "remove the join us and
    // nest menu under the about menu item" asked to delete a link literally called
    // "join us and nest menu under the about" — which of course matched nothing.
    .replace(/\s+(?:nav(?:igation)?|menu|drop-?down|sub-?menu)?\s*(?:from|under(?:neath)?|beneath|inside|within)\s+.*$/i, '')
    .replace(/\s+(?:link|item|button|page)$/i, '')
    .replace(/\s+(?:nav(?:igation)?|menu|header|footer)$/i, '')
    .trim();
  if (!label || /^(it|this|that)$/i.test(label)) return null;
  return { kind: 'remove-nav-link', label, target };
}

// ── logo resize ───────────────────────────────────────────────────────────────
// "make the logo bigger" / "shrink the logo" / "set the logo to 56px". The header
// logo height is a site-wide STYLE setting (the header chrome reads it), not a block
// prop — so this is handled deterministically against the style API, never the AI.
// Requires the word "logo" so it never fires on other resize requests.
const LOGO_BIGGER = /\b(bigger|larger|grow|increase|enlarge|huge|biggest)\b/i;
const LOGO_SMALLER = /\b(smaller|shrink|reduce|decrease|tiny|tinier|smallest)\b/i;
export function parseLogoResize(prompt: string): LogoResizeCommand | null {
  const p = prompt.trim();
  if (!/\blogos?\b/i.test(p)) return null;
  // Absolute: "... 56px" / "... to 56 px" / "... 56 pixels"
  const px = p.match(/\b(\d{1,3})\s*(?:px|pixels?)\b/i);
  if (px) {
    const n = parseInt(px[1], 10);
    if (n >= 8 && n <= 400) return { kind: 'logo-resize', mode: 'set', px: n };
  }
  if (LOGO_BIGGER.test(p)) return { kind: 'logo-resize', mode: 'bigger' };
  if (LOGO_SMALLER.test(p)) return { kind: 'logo-resize', mode: 'smaller' };
  return null;
}

// ── simple edits (color / literal text / visibility) ──────────────────────────
const EDIT_VERB = /\b(make|set|change|turn|paint|colou?r|update|rename)\b/i;
// Words that signal a GENERATIVE request → must go to AI, not a literal set.
const GENERATIVE_CUE =
  /\b(more|less|better|catch(y|ier)|professional|modern|luxur(y|ious)|inviting|friendl(y|ier)|exciting|engaging|compelling|punchy|sound|feel|look|like|something|a\s+bit|nicer|cleaner|warmer|stronger|shorter|longer)\b/i;

/** Parse a simple, literal edit. Returns null for anything generative/ambiguous. */
export function parseSimpleEdit(prompt: string): SimpleEdit | null {
  const p = prompt.trim();

  // visibility
  if (/\b(hide|unpublish)\b/i.test(p) && !/\bdelete\b/i.test(p)) return { kind: 'visibility', hidden: true };
  if (/\b(show|unhide|reveal|make\s+visible)\b/i.test(p)) return { kind: 'visibility', hidden: false };

  // color: needs an edit verb + a concrete color
  if (EDIT_VERB.test(p)) {
    const value = extractColor(p, { requireCue: true });
    if (value) {
      // We can only deterministically map background vs text color. A button/CTA/accent/link
      // color — or a site-wide / two-color brand change ("the whole site navy and gold") — has
      // no simple-edit field, and defaulting it to the section BACKGROUND was wrong. Defer those
      // to the AI, which targets the right field / brand tokens instead of mis-scoping.
      if (/\b(button|cta|accent|link)s?\b/i.test(p) || /\b(?:whole|entire)\s+site\b|\bsite-?wide\b/i.test(p)) return null;
      const target: ColorTarget = /\b(text|font)\s*colou?r\b|\btext\b/i.test(p) ? 'text' : 'background';
      return { kind: 'color', target, value };
    }
  }

  // literal text set: change/set/rename the <field> to <value>. Quoted value, or
  // an unquoted value with NO generative cue (so rewrites still go to AI).
  // An optional block-name qualifier ("the HERO heading", "the ABOUT title") may sit between
  // "the" and the field — without this slot the whole edit fell through to the paid AI.
  const m = p.match(
    /\b(?:change|set|update|rename|make)\b\s+(?:the\s+)?(?:[a-z][a-z-]*\s+)?(heading|headline|title|subheading|subtitle|tagline|button(?:\s*(?:label|text))?|cta)\s+(?:to|=|:)\s+(.+)$/i,
  );
  if (m) {
    const fieldRaw = m[1].toLowerCase();
    let raw = m[2].trim();
    const quoted = raw.match(/^["']([^"']+)["']\s*$/);
    if (quoted) {
      raw = quoted[1];
    } else if (GENERATIVE_CUE.test(raw) || raw.length > 80) {
      return null; // generative / too long → let the AI handle it
    }
    // Check sub*/tagline FIRST — "subheading"/"subtitle" contain "head"/"title",
    // so testing heading first would mis-map them to the H1.
    const field =
      /^(sub|tagline)/.test(fieldRaw) ? 'subheading'
        : /head|title/.test(fieldRaw) ? 'heading'
          : 'button';
    return { kind: 'text', field, value: raw.replace(/[."'\s]+$/, '').trim() };
  }
  return null;
}

// ── schema-aware resolution (keeps the parser pure of block specifics) ─────────
export interface SchemaFieldLite {
  key: string;
  label?: string;
  widget?: string;
}

/** The block prop that holds the given color target, or null if the block has none. */
export function resolveColorField(fields: SchemaFieldLite[], target: ColorTarget): string | null {
  const want = target === 'background' ? /background/i : /text/i;
  const f = fields.find((x) => x.widget === 'color' && (want.test(x.key) || want.test(x.label ?? '')));
  return f?.key ?? null;
}

/** The single-line text prop matching the field hint (heading/subheading/button). */
export function resolveTextField(fields: SchemaFieldLite[], hint: string): string | null {
  const patt =
    hint === 'heading' ? /heading|headline|title/i
      : hint === 'subheading' ? /subheading|subtitle|tagline/i
        : /label|button|cta/i;
  const f = fields.find(
    (x) =>
      (x.widget === 'text' || x.widget === 'textarea' || x.widget === 'url' || x.widget === undefined) &&
      (patt.test(x.key) || patt.test(x.label ?? '')),
  );
  return f?.key ?? null;
}

// ── chat media attachments (upload a file in chat → apply it to a block) ────────
export interface ChatAttachment {
  url: string;
  kind: 'image' | 'video' | 'doc';
  name: string;
}

export type MediaFieldHint = 'background' | 'logo' | 'poster' | 'video' | 'gallery' | 'photo' | null;

const MEDIA_HINTS: Array<[RegExp, Exclude<MediaFieldHint, null>]> = [
  [/\b(background|bg|backdrop|hero\s*image|cover\s*image)\b/i, 'background'],
  [/\blogos?\b/i, 'logo'],
  [/\b(poster|thumbnail|placeholder)\b/i, 'poster'],
  [/\b(video|mp4|clip|footage|reel)\b/i, 'video'],
  [/\b(gallery|carousel|slides?|slideshow)\b/i, 'gallery'],
  [/\b(photos?|pictures?|images?|headshots?|portraits?|pics?)\b/i, 'photo'],
];

/** A field-placement hint parsed from the prompt ("use this as the background"). */
export function parseMediaTarget(prompt: string): MediaFieldHint {
  for (const [re, hint] of MEDIA_HINTS) if (re.test(prompt)) return hint;
  return null;
}

/** Heuristic: does an uploaded image's FILENAME look like an agent headshot/portrait?
 *  The filename is the only client-side signal for a dropped photo — used to offer a more
 *  useful placement than a generic "select a section". Conservative (clear tokens only). */
export function isHeadshotFilename(name: string): boolean {
  // Underscore is a regex word char, so "DawnPfaff_Portrait" has NO \b before "Portrait" — the
  // token check missed underscore-separated names. Normalize "_" to a space first.
  const base = name.replace(/\.[a-z0-9]+$/i, '').replace(/_/g, ' ');
  return (
    /\b(headshot|portrait|selfie|mugshot)\b/i.test(base) ||
    /\b(profile|agent|my|staff|team)[-_ ]?(photo|pic|picture|headshot|portrait)\b/i.test(base)
  );
}

const IMAGE_WIDGETS = new Set(['image-url']);
const LIST_WIDGETS = new Set(['image-list']);
const VIDEO_WIDGETS = new Set(['video-url']);

/**
 * Pick the block prop that should receive an uploaded file, given an optional field
 * hint from the prompt and the file kind. `list` = the prop is an array (append, e.g.
 * a hero carousel). Returns null when the block has no matching media field.
 */
export function resolveMediaField(
  fields: SchemaFieldLite[],
  hint: MediaFieldHint,
  kind: 'image' | 'video',
): { key: string; list: boolean } | null {
  const byWidget = (set: Set<string>) => fields.filter((f) => !!f.widget && set.has(f.widget));
  if (kind === 'video') {
    const v = byWidget(VIDEO_WIDGETS)[0];
    return v ? { key: v.key, list: false } : null;
  }
  const images = byWidget(IMAGE_WIDGETS);
  const lists = byWidget(LIST_WIDGETS);
  if (hint === 'gallery' && lists[0]) return { key: lists[0].key, list: true };
  if (hint && images.length > 1) {
    const patt =
      hint === 'background' ? /background|hero|cover|backdrop/i
        : hint === 'logo' ? /logo/i
          : hint === 'poster' ? /poster|thumb/i
            : /image|photo|url|src/i;
    const m = images.find((f) => patt.test(f.key) || patt.test(f.label ?? ''));
    if (m) return { key: m.key, list: false };
  }
  if (images[0]) return { key: images[0].key, list: false };
  if (lists[0]) return { key: lists[0].key, list: true };
  return null;
}

// ── one-shot page plan (create a page + populate it + optionally link it) ───────
// The "cross-cutting" command from the AI-chat plan: turn a single compound
// request like "add a listings page with a map and a nav link" (or "…and link it
// in the footer") into ONE action — create the page, add the named sections, and
// (optionally) link it in the nav and/or footer — instead of the old 2–3 step
// flow. Deterministic: only fires when concrete block types are named or a
// nav/footer link is requested. Descriptive requests ("a page that showcases our
// luxury homes") return null and fall to the AI planner / blank add.
export interface PagePlanCommand {
  kind: 'page-plan';
  name: string; // '' when no name could be extracted (caller supplies a default)
  blockTypes: BlockType[]; // deterministic sections to add when there's no descriptor
  addNavLink: boolean; // also add a link to this page in the site nav
  /** Nest that nav link under this existing menu label ("…and nest it under the About menu item").
   *  Only set when a link is being added — a parent with no link is meaningless. */
  navParent?: string;
  addFooterLink: boolean; // also add a link to this page in the site footer
  descriptor: string | null; // a free-text content brief → AI-generate the body
  edit: boolean; // EDIT intent ("edit/rewrite the X page") → rewrite an EXISTING page, even non-empty
}

// Noun → block type, for parsing a page's requested sections. Ordered
// specific-first. Deliberately excludes 'nav'/'raw_html' (a "nav link" request is
// handled separately; raw HTML isn't something you'd ask for by name here).
const PAGE_BLOCK_KEYWORDS: Array<[RegExp, BlockType]> = [
  [/\bcontact(\s*(form|us|section))?\b/, 'contact_form'],
  [/\b(hero|banner)\b/, 'hero'],
  [/\blistings?\b|\bproperties\b|\bproperty\s+(grid|list|cards)\b/, 'listings'],
  [/\b(agents?|realtors?|brokers?|team|roster)\b/, 'agents'],
  [/\boffices?\b|\blocations?\b|\bbranches?\b/, 'offices'],
  [/\bcommunit(?:y|ies)\b|\bneighbou?rhoods?\b/, 'communities'],
  [/\bblog\b|\barticles?\b|\bnews\b/, 'blog_list'],
  [/\bstats?\b|\bnumbers\b|\bmetrics?\b|\bstatistics\b/, 'stats'],
  [/\bfeature(s|\s*grid)?\b|\bwhy\s+(choose|us)\b|\bbenefits?\b/, 'feature_grid'],
  [/\btestimonials?\b|\breviews?\b|\bwhat\s+(our\s+)?clients\b/, 'testimonials'],
  // home-valuation + search-alert exist as block types but were unreachable from chat (Phase B).
  // search-alert is listed BEFORE the generic `search` entry so "saved search" → search-alert.
  [/\bhome\s*valuations?\b|\bhome\s+value\b|\bwhat'?s?\s+(my|your)\s+home\s+worth\b|\bcma\b|\bvaluation\s+(tool|form|widget)\b/, 'home-valuation'],
  [/\bsaved\s+searche?s?\b|\bsearch\s+alerts?\b|\blisting\s+alerts?\b|\bproperty\s+alerts?\b/, 'search-alert'],
  [/\bmap\b/, 'map'],
  [/\bsearch(\s*(bar|box))?\b/, 'search'],
  [/\b(gallery|image|photo|media)s?\b/, 'image'],
  [/\bcall.to.action\b|\bcta\b/, 'cta'],
  [/\b(about|bio|intro|text\s*section|paragraph|story)\b/, 'rich_text'],
  [/\bfooter\b/, 'footer'],
];

/** Every block type named in `text`, de-duped, in keyword-priority order. */
export function detectBlockTypesIn(text: string): BlockType[] {
  const p = text.toLowerCase();
  const found: BlockType[] = [];
  for (const [re, type] of PAGE_BLOCK_KEYWORDS) {
    if (re.test(p) && !found.includes(type)) found.push(type);
  }
  return found;
}

export interface AddSectionCommand {
  kind: 'add-section';
  blockType: BlockType;
  position: 'start' | 'end';
}

/**
 * "add a hero section to this page", "put a testimonials block here", "add a listings section at
 * the top" → add a BLOCK of that type to the CURRENT page (NOT create a page — the bug where
 * "add a hero section" made a page). Conservative on purpose: fires only with a real block type
 * AND an explicit section/"here"/"this page" signal, so it never hijacks "add a contact page" or
 * a media/edit intent. Richer phrasings ("add a hero that says X") still flow to the AI agent.
 */
export function parseAddSection(prompt: string): AddSectionCommand | null {
  const p = prompt.trim();
  if (!ADD_VERB.test(p)) return null;
  // A genuine page-CREATE ("add a contact page with a search section") owns this — defer so a
  // page (not a stray block) gets made. parsePagePlan already returns null for section-adds that
  // merely target an existing page ("…to this page"), so this only steps aside for real creates.
  const pagePlan = parsePagePlan(p);
  if (pagePlan && !pagePlan.edit) return null;
  const toCurrentPage =
    /\b(?:to|on|onto|in)\s+(?:this|the\s+(?:current|home))\s+page\b/i.test(p) ||
    /\b(?:over\s+)?here\b/i.test(p) ||
    // "…to/at the top/bottom (of this page)" — the old check only matched "AT the top/bottom", so
    // "add a photo gallery TO the bottom of this page" fell through to the paid AI.
    /\b(?:to|at)\s+the\s+(?:top|bottom|start|end|beginning)\b/i.test(p);
  // A "section/block" word OR a concrete component noun (form/tool/widget/signup/bar) — so
  // "add a contact form", "add a home valuation tool", "add a saved search signup" are reachable
  // deterministically, not just "… section". Still requires a real block type (below), so a bare
  // "add a form"/"add a tool" with no recognized type falls through to the AI.
  const sectionWord = /\bsections?\b|\bblocks?\b|\bforms?\b|\btools?\b|\bwidgets?\b|\bsign[\s-]?ups?\b|\bbars?\b/i.test(p);
  if (!sectionWord && !toCurrentPage) return null; // require an explicit section / location signal
  // A CONTENT DESCRIPTOR means the user wants copy written, not an empty scaffold —
  // "add a hero that says Welcome Home", "add a note about the contact form here".
  // Those must flow to the AI (which writes the copy); this parser would silently
  // drop the words — or worse, "a note about the contact form" became a second FORM.
  // ("about" only counts when it ISN'T naming the about section itself.)
  if (/\b(?:that says|saying|explaining|describing|mentioning|titled|headed|with (?:the )?(?:text|words)|about (?!(?:us |our |me )?(?:us |section|block)\b))/i.test(p)) {
    return null;
  }
  // A positional ANCHOR ("below the listing section", "above the hero block", "under the contact
  // section") names WHERE to place the new section — its type must NOT be read as the type to ADD.
  // Strip it first, so "add a section for agents below the listing section" adds AGENTS, not
  // listings (which only outranks agents by keyword-priority order in detectBlockTypesIn).
  // The anchor is a trailing "below/above/under/after <the X> (section)?" clause. Matched to the end
  // (that's where a "place it below the listings" phrase sits) with the "section" word optional, so
  // both "below the listing section" AND "below the listings" are stripped before type detection.
  const withoutAnchor = p.replace(
    /\s+\b(?:below|above|under(?:neath)?|over|beneath|after|before)\s+(?:the\s+)?[a-z0-9][a-z0-9 ]*?(?:\s+(?:section|block|area))?\s*[.!?]*$/i,
    ' ',
  );
  const types = detectBlockTypesIn(withoutAnchor);
  if (types.length === 0) return null;
  // A COMPOUND request names more than one block type joined by a conjunction ("change the hero
  // AND add a testimonials section"). Taking types[0] silently added the wrong one (keyword-
  // priority, not prompt order) and dropped the rest — defer to the agent, which sequences every
  // clause. Requires "and": a single phrase that merely matches two keyword patterns ("saved
  // search signup" → search-alert + search) is ONE block, not a compound.
  if (types.length > 1 && /\band\b/i.test(withoutAnchor)) return null;
  const position: 'start' | 'end' = /\b(?:top|start|beginning|above|first)\b/i.test(withoutAnchor) ? 'start' : 'end';
  return { kind: 'add-section', blockType: types[0], position };
}

// ── section REPOSITION with a clarify (ask WHERE, don't guess) ───────────────────
// Reported: "move the agent section up to under the listings" went to the paid agent, which guessed
// "top". A relative/unclear spot should ASK with position choices instead of guessing.
export interface SectionMoveCommand {
  kind: 'section-move';
  section: string; // the section to move (type/name); the handler asks WHERE
}
const SECTION_MOVE_VERB = /\b(move|reorder|reposition|shift|put|place|bump|relocate)\b/i;

export type SectionPlacement =
  | { section: string; pos: 'top' | 'bottom' }
  | { section: string; pos: 'after'; anchor: string };

/** The armed phrase a clarify chip sends → a DETERMINISTIC placement (never re-clarifies). */
export function placementPrompt(section: string, pos: 'top' | 'bottom' | 'after', anchor?: string): string {
  if (pos === 'top') return `Move the ${section} section to the very top`;
  if (pos === 'bottom') return `Move the ${section} section to the very bottom`;
  return `Move the ${section} section to just below the ${anchor} section`;
}
export function parsePlacement(prompt: string): SectionPlacement | null {
  const p = prompt.trim();
  const t = p.match(/^move the (.+?) section to the very (top|bottom)\b/i);
  if (t) return { section: t[1].trim(), pos: t[2].toLowerCase() as 'top' | 'bottom' };
  const a = p.match(/^move the (.+?) section to just below the (.+?) section\b/i);
  if (a) return { section: a[1].trim(), pos: 'after', anchor: a[2].trim() };
  return null;
}

/** "move the agents section under the listings" / "reposition the hero" — a SECTION move whose
 *  destination is relative/unclear → the handler asks WHERE. Clean top/bottom/up/down moves are
 *  left to the existing structural parser; nav links + armed placements are excluded. */
export function parseSectionMove(prompt: string): SectionMoveCommand | null {
  const p = prompt.trim();
  if (!SECTION_MOVE_VERB.test(p) || !/\b(section|block)\b/i.test(p)) return null;
  if (/\b(nav(?:igation)?|menu|header|footer|link|dropdown)\b/i.test(p)) return null; // nav, not a block
  if (parsePlacement(p)) return null; // an armed placement executes — never clarify it
  const m = p.match(/\b(?:move|reorder|reposition|shift|put|place|bump|relocate)\s+(?:the\s+)?([a-z0-9][a-z0-9 ]*?)\s+(?:section|block)\b/i);
  const section = m ? m[1].trim() : '';
  if (!section || /^(it|this|that)$/i.test(section)) return null;
  // Clarify ONLY when the destination is RELATIVE ("under/below/above the X", the reported bug) or
  // MISSING entirely ("move the hero section"). A clean "to the top/bottom" or "up/down" is left to
  // the existing structural move parser, which handles it deterministically ($0).
  const hasRelative = /\b(?:under(?:neath)?|below|above|beneath|next\s+to|near|between|before|after)\b/i.test(p);
  const hasCleanDest = /\b(?:to\s+(?:the\s+)?(?:top|bottom|start|end|beginning)|up|down|higher|lower)\b/i.test(p);
  return hasRelative || !hasCleanDest ? { kind: 'section-move', section } : null;
}

/**
 * True when the prompt asks to link the page in the given chrome (nav or footer).
 * An EXPLICIT location ("in/to/on the footer") wins; the loose noun ("a nav link",
 * "footer link") is a fallback. "menu item" is ambiguous, so it only counts as a
 * nav request when the footer isn't mentioned (otherwise "a menu item link in the
 * footer" means a FOOTER link, not a nav one).
 */
function wantsLinkIn(p: string, where: 'nav' | 'footer'): boolean {
  if (where === 'footer') {
    return (
      /\b(?:in|to|on|into)\s+(?:the\s+)?footer\b/i.test(p) || // "link it in the footer"
      /\bfooter\s+(?:link|item)\b/i.test(p) // "a footer link"
    );
  }
  return (
    /\b(?:in|to|on|into)\s+(?:the\s+)?(?:nav(?:igation)?|menu)\b/i.test(p) || // "in the nav", "to the menu"
    /\b(?:nav(?:igation)?|menu)\s+link\b/i.test(p) || // "a nav link", "menu link"
    (/\bmenu\s+item\b/i.test(p) && !/\bfooter\b/i.test(p)) // "a menu item" — nav only if not a footer request
  );
}

/**
 * Tidy an extracted page name: drop surrounding quotes, cut at the first clause
 * boundary (comma / "and …" / "with …" / a trailing imperative like "add …"), and
 * cap the length. Stops a compound sentence ("the privacy policy for LWR, add a
 * footer link") from becoming the page name.
 */
export function cleanPageName(raw: string): string {
  let s = raw.trim().replace(/^["']+|["']+$/g, '').trim();
  // Split off trailing clauses — but NOT a bare "and": "terms and conditions" is ONE
  // name. "and" only ends the name when it introduces an imperative ("… and link it").
  s = s.split(/\s*[,;]\s*|\s+(?:then|plus|also|with|that|including|featuring)\s+|\s+(?:and\s+)?(?:add|put|link|create|make|include|set)\s+/i)[0].trim();
  s = s.replace(/\s+and$/i, '').trim();
  // Drop a leading grammatical article ("a privacy policy" / "the about" → "privacy
  // policy" / "about"). It comes from "add a page for A privacy policy", not the name.
  s = s.replace(/^(?:a|an|the)\s+/i, '').trim();
  // Drop a leading SCOPE word. "rewrite the whole About page" captured the name as "whole
  // About", which matched no existing page — so the edit path could not reuse the real About
  // page and created a duplicate instead, seeded it with the raw brief, added it to the menu
  // and published it. The user asked to rewrite one page and got a second one live on their
  // site. These three are never page names; "new" deliberately is NOT stripped, because "New
  // Homes" and "New Construction" are.
  s = s.replace(/^(?:whole|entire|full)\s+/i, '').trim();
  if (s.length > 50) s = s.slice(0, 50).trim();
  return s;
}

/**
 * A free-text content brief from the prompt (e.g. "about my 20 years selling
 * waterfront homes"), or null. Strips the name spec ("called X") and any
 * nav/footer link instructions so only the topic remains — that's what the AI
 * uses to generate the page body.
 */
export function extractDescriptor(prompt: string): string | null {
  const m = prompt.match(/\bpage\b(.*)$/i);
  const rest = (m ? m[1] : '')
    .replace(/\b(?:called|named|titled)\s+["']?[a-z0-9][a-z0-9 &'-]*["']?/gi, ' ') // "called X"
    .replace(/\b(?:and\s+|also\s+)?(?:add|put|include|link|with)\b[^.?!]*?\b(?:nav(?:igation)?|menu|footer)\b[^.?!]*/gi, ' ') // "…link in the footer"
    .replace(/\bin\s+the\s+(?:nav(?:igation)?|menu|footer)\b/gi, ' ')
    .replace(/^[\s,;:.–-]+/, '')
    .replace(/[\s,;:.–-]+$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  const words = rest.split(/\s+/).filter(Boolean);
  return words.length >= 2 && rest.length > 6 ? rest : null;
}

/**
 * Which EXISTING page does this prompt name, if any?
 *
 * The gap this closes (field report, twice, with screenshots): "can you write a much more
 * detailed about us page …" parsed to nothing here (parsePagePlan wants its known shapes), so
 * it rode the generative path to `generate_page` — which builds onto whichever page is OPEN.
 * With Home open, the home page got seven about-us sections. The page the user NAMED was
 * never consulted by anything.
 *
 * Deliberately strict: the page name must be immediately followed (≤2 filler words) by the
 * word "page", so "the about us page" matches a page named About while "write a page about
 * our team" — where "about" is a preposition — matches nothing. A conservative miss keeps
 * today's behavior; a false positive would redirect the build, which is worse.
 */
export function resolveNamedPageInPrompt(
  prompt: string,
  pages: ReadonlyArray<{ id?: string; name?: string; title?: string; slug?: string }>,
): { id?: string; name: string } | null {
  const p = ` ${prompt.toLowerCase()} `;
  if (!/\bpage\b/.test(p)) return null;
  for (const page of pages) {
    const label = String(page.name ?? page.title ?? '').trim();
    const slug = String(page.slug ?? '').replace(/^\/+|\/+$/g, '').replace(/-/g, ' ').trim();
    for (const candidate of [label, slug]) {
      if (candidate.length < 3) continue; // "/" (home) and one-letter names: too easy to hit
      const esc = candidate.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`\\b${esc}\\b(?:\\s+\\w+){0,2}?\\s+page\\b`, 'i');
      if (re.test(p)) return { id: page.id, name: label || candidate };
    }
  }
  return null;
}

/**
 * "add my contact info(rmation) [from my brand] to the footer/site" — apply the BRAND's
 * saved contact details, deterministically.
 *
 * Brand Manager is the source of truth for the footer's contact (per the product rules and
 * resolveSiteFooter), and the values are sitting in the site's own config — yet this request
 * used to ride the agent, which ASKED for the email/phone/address the system already had,
 * and when told "from my brand" INVENTED "mtarealty@example.com" instead of reading
 * mta@mta.com. A model must never be in this loop: the data is structured, the write is
 * structured, and the only judgement call (the email confirm) is already an armed chip.
 *
 * Deliberately narrow: requires "my … contact info/information/details" so "add a contact
 * form/section/page" (block and page ops) never match, and steps aside when the prompt
 * carries an explicit email address — that's a set-footer-field with a given value.
 */
export interface BrandContactCommand {
  /** The brand chosen from the picker chips (its canonical prompt quotes the name), or null —
   *  the first ask, which answers with one chip per brand (product call: selection is always
   *  explicit, even with a single brand). */
  brandName: string | null;
}

export function parseBrandContactToFooter(prompt: string): BrandContactCommand | null {
  const p = prompt.trim();
  if (/@/.test(p)) return null; // an explicit address → the existing set-field paths
  if (!/\b(add|put|use|set|show|include|display)\b/i.test(p)) return null;
  // The picker chip's canonical phrasing: add contact info from my "Name" brand to the footer.
  const named = p.match(/\bfrom\s+(?:my|our|the)\s+["“']([^"”']{1,80})["”']\s+brand\b/i);
  if (named && /\bcontact\s+(?:info(?:rmation)?|details?)\b/i.test(p)) {
    const brandName = named[1]!.trim();
    if (brandName) return { brandName };
  }
  if (!/\b(?:my|our)\b[\w\s'’]{0,24}\bcontact\s+(?:info(?:rmation)?|details?)\b/i.test(p)) return null;
  return { brandName: null };
}

/**
 * "add my company name to the footer" — the NAME lives in Brand Manager, so like the contact
 * flow this is a brand pick, never a model's guess. A live run let the cheap planner take this
 * exact phrasing and it wrote the LITERAL text "your company name" into the footer (models echo
 * the possessive back; there is no value in the prompt to extract). Explicit picker, same
 * product call as contact: one chip per brand, even when there is only one.
 */
export interface BrandNameCommand {
  /** The brand chosen from the picker chips, or null — the first ask (answers with chips). */
  brandName: string | null;
}

export function parseBrandNameToFooter(prompt: string): BrandNameCommand | null {
  const p = prompt.trim();
  // The picker chip's canonical phrasing: use my "Name" brand name in the footer.
  const named = p.match(/\buse\s+(?:my|our|the)\s+["“']([^"”']{1,80})["”']\s+brand\s+name\b/i);
  if (named) {
    const brandName = named[1]!.trim();
    if (brandName) return { brandName };
  }
  if (!/\bfooter\b/i.test(p)) return null;
  if (!/\b(add|put|use|set|show|include|display|write)\b/i.test(p)) return null;
  // Requires the possessive — "set the footer company name to X" carries an explicit value
  // and stays with parseBlockFieldEdit.
  if (!/\b(?:my|our)\s+(?:company|business|brand)\s+name\b/i.test(p)) return null;
  return { brandName: null };
}

export function parsePagePlan(prompt: string): PagePlanCommand | null {
  const p = prompt.trim();
  const isAdd = ADD_VERB.test(p);
  const isEdit = PAGE_EDIT_VERB.test(p);
  if ((!isAdd && !isEdit) || !/\bpage\b/i.test(p)) return null;
  // "add a link/menu item to the X page" is a NAV command — the page is the link's TARGET,
  // not a page to create. Defer to parseAddNavLink / the agent. Fire ONLY when the nav-add is
  // the PRIMARY op, i.e. the nav noun (link / menu item) comes BEFORE the first "page" word:
  //   "add a menu item called About Us … to the about page"  → defer (nav first, page is target)
  //   "add a privacy policy page … add a footer menu item link" → still CREATE the page (page first)
  const navAddM = p.match(
    /\b(?:add|put|create|insert|include|need|want)\s+(?:an?\s+)?(?:link|menu\s*item|nav\s*item|sub-?(?:item|link|menu)|drop-?down(?:\s+(?:item|link))?|menu\s+entry)\b/i,
  );
  // A page-CREATE signal is an INDEFINITE "a/an/another [name] page" — NOT "the/this about page",
  // which is a reference/target. Defer to the nav path when a nav-add exists and there's either
  // no page-create signal, or the nav-add comes first.
  const pageCreateM = p.match(/\b(?:a|an|another)\s+[a-z0-9][a-z0-9 &'-]*?\s+page\b/i);
  if (isAdd && navAddM && (!pageCreateM || (navAddM.index ?? 0) < (pageCreateM.index ?? Infinity))) return null;
  // EDIT intent only when an edit verb is present WITHOUT a create verb ("edit the
  // about page", not "make a new page and edit it" — the latter creates).
  const edit = isEdit && !isAdd;
  // Name: an explicit "a [Name] page" wins (the word(s) before "page"); else
  // "the/my [Name] page" (common for edits), else "page called/named X".
  // cleanPageName trims trailing clauses either way. Compute the explicit-name match
  // FIRST — it decides both the section-add guard and the name.
  const aXpage = p.match(/\b(?:a|an|another)\s+([a-z0-9][a-z0-9 &'-]*?)\s+page\b/i);
  const theXpage = p.match(/\b(?:the|my|our|this)\s+([a-z0-9][a-z0-9 &'-]*?)\s+page\b/i);
  const called = p.match(/(?:called|named|titled)\s+["']?([^"'\n]+?)["']?(?:\s+(?:with|that|including|featuring|and)\b|[,;]|$)/i);
  // A genuine new-page name — reject a section-add phrase ("a hero TO the page", "a contact
  // form ON the page") whose capture drags in a preposition, so only a real name survives.
  const newPageName =
    aXpage && !PREPOSITION_IN_NAME.test(aXpage[1]) ? aXpage[1] : '';

  // "add a hero to the page" / "add content on this page" targets an EXISTING page (a
  // section/content add), not a new one — but "edit the X page" legitimately uses "the
  // page", and "add a [Name] page, on this page I need …" is a CREATE that merely REFERS
  // to the new page as "this page". Only bail when there's no explicit new-page name.
  // An explicit "called/named/titled X" IS a real new-page name (it just arrived that way
  // instead of the "a [Name] page" form), so a trailing "…content on that page" — which
  // refers to the NEW page — must not trip this bail. Fixes "add a page called Join Us and
  // create content on that page" being dropped (which let Custom Design rebuild the homepage).
  const hasExplicitNewName = Boolean(newPageName) || Boolean(called?.[1]?.trim());
  // The name between the determiner and "page" used to defeat this: "to the page" bailed,
  // "to the ABOUT page" did not — so "add a section to the about page" was read as a page-plan
  // for a page called "about", and because `edit` is false and the real About page has content,
  // it would have CREATED a duplicate. The same shape that published "New About". Found by the
  // routing table before anyone hit it.
  if (!edit && !hasExplicitNewName && PAGE_AS_TARGET.test(p)) return null;

  let name = '';
  if (newPageName) name = newPageName;
  else if (theXpage && !/\b(to|with|that|and|featuring|including)\b/i.test(theXpage[1])) name = theXpage[1];
  else if (called) name = called[1];
  name = cleanPageName(name);

  // A page whose name implies a section (about/contact/listings/privacy/…).
  const lname = name.toLowerCase();
  const implied: BlockType | null = /\blisting/.test(lname)
    ? 'listings'
    : /\bcontact/.test(lname)
      ? 'contact_form'
      : /\b(agents?|realtors?|brokers?|team|roster)\b/.test(lname)
        ? 'agents'
        : /\b(offices?|locations?|branches?)\b/.test(lname)
          ? 'offices'
          : /\b(communit(?:y|ies)|neighbou?rhoods?)\b/.test(lname)
            ? 'communities'
            : /\b(blog|articles?|news)\b/.test(lname)
              ? 'blog_list'
              : /\b(about|bio|privacy|policy|terms|disclaimer|legal|story)\b/.test(lname)
                ? 'rich_text'
                : null;

  // Strip the nav/footer LINK instruction FIRST, so "…link it in the footer" /
  // "with a nav link" aren't read as a request for a footer/nav BLOCK. That false
  // positive both added a stray footer block AND suppressed the content brief
  // below (since a non-empty explicitBlocks skips the descriptor). Link intent is
  // detected separately via wantsLinkIn(p).
  const pBlocks = p
    // "…(and) link it/this/the page in/to/on the nav/footer/menu"
    .replace(/\b(?:and\s+)?link\b[^.?!]*?\b(?:in|to|on)\s+(?:the\s+)?(?:nav(?:igation)?|menu|footer)\b/gi, ' ')
    // "…(and) add/put/include [it|a link|the page] in/to/on the nav/footer/menu" (bounded so the LEADING add-verb isn't eaten)
    .replace(/\b(?:and\s+)?(?:add|put|include)\b[^.?!]{0,24}?\b(?:in|to|on)\s+(?:the\s+)?(?:nav(?:igation)?|menu|footer)\b/gi, ' ')
    // "(with)(a/an) nav/menu/footer link|item"
    .replace(/\b(?:with\s+)?(?:an?\s+)?(?:nav(?:igation)?|menu|footer)\s+(?:link|item)\b/gi, ' ')
    // "…and a nav/footer link"
    .replace(/\band\s+(?:a\s+)?(?:nav(?:igation)?|menu|footer)\s+link\b/gi, ' ')
    // bare "in/to/on the nav/menu/footer"
    .replace(/\b(?:in|to|on)\s+the\s+(?:nav(?:igation)?|menu|footer)\b/gi, ' ');

  // Explicit sections ("with a map and a contact form"), link targets, and a
  // free-text content brief (only when no explicit blocks were named).
  const withM = pBlocks.match(/\b(?:with|including|featuring|that\s+(?:has|includes|shows)|containing)\b(.*)$/i);
  const explicitBlocks = withM ? detectBlockTypesIn(withM[1]) : [];
  const addNavLink = wantsLinkIn(p, 'nav');
  const addFooterLink = wantsLinkIn(p, 'footer');
  // The topic brief. When explicit sections are named ("with a hero and a contact
  // form"), take the text BEFORE that clause so the brief is the TOPIC ("about buyer
  // services"), not the section list — and KEEP it alongside explicit blocks so those
  // sections get topical AI copy instead of placeholder text.
  const descriptor =
    withM && explicitBlocks.length > 0
      ? extractDescriptor(pBlocks.slice(0, withM.index ?? pBlocks.length))
      : extractDescriptor(p);

  // An EDIT needs a target page name (we rewrite THAT page). An ADD needs something
  // beyond a bare unknown page: explicit sections, a nav/footer link, a known page
  // type, or a content brief — else a bare "add a page called Resources" falls
  // through to the simple add-page path.
  if (edit) {
    if (!name) return null;
  } else if (explicitBlocks.length === 0 && !addNavLink && !addFooterLink && !implied && !descriptor) {
    return null;
  } else if (explicitBlocks.length === 0 && !addNavLink && !addFooterLink && !name && /\bpage\s+for\b/i.test(p)) {
    // "make a page FOR X" — no explicit "[Name] page", no sections, no link. Here "for"
    // NAMES the page ("terms and conditions"); it is NOT a content brief to AI-generate.
    // Defer to the instant, no-AI add-page path (parseAddPage extracts the name). Fixes
    // "make a page for terms and conditions" spinning up slow page generation. (An
    // explicit "a privacy policy page for X" sets its name above, so it still plans.)
    return null;
  }

  const blockTypes = [...explicitBlocks];
  if (implied && !blockTypes.includes(implied)) blockTypes.unshift(implied);

  // If no explicit name was given but we have a topic, derive a short page name from
  // it ("about buyer services" → "Buyer Services") so the page isn't called "New Page".
  if (!name && descriptor) {
    name = cleanPageName(descriptor.replace(/^(?:about|on|regarding|for|covering|our|the)\s+/i, ''));
  }

  // "…and nest it under the About menu item" — where the new page's link goes. Carried so the
  // whole request lands: the nav parser used to claim this sentence outright and produce a
  // dropdown item with no page behind it, and the page path that replaced it would otherwise
  // create the page and drop the nesting on the floor.
  //
  // A parent IMPLIES the link: 'add a page called "join us" and nest it under about' names no
  // link explicitly, so addNavLink was false, the parent was never computed, and — worse — the
  // nesting clause fell into `descriptor` and was sent to the AI as the page's content brief.
  const navParent = navParentFromPrompt(p);
  const wantNavLink = addNavLink || Boolean(navParent);
  // The nesting clause is an INSTRUCTION, not content — strip it from the brief.
  const brief = navParent && descriptor
    ? descriptor.replace(/(?:^|\s)(?:and\s+|then\s+)?nest(?:ed)?\b[^.!?]*$/i, '').trim() || null
    : descriptor;

  return { kind: 'page-plan', name, blockTypes, addNavLink: wantNavLink, addFooterLink, descriptor: brief, edit, ...(navParent ? { navParent } : {}) };
}

/**
 * Data blocks pull their content LIVE from the tenant's integration / saved content
 * (listings, agents, offices, communities, blog) — the AI must never invent it.
 * So a page that includes a data block is built deterministically, not from copy.
 */
export const DATA_BLOCK_TYPES: BlockType[] = ['listings', 'agents', 'offices', 'communities', 'blog_list'];

export function isDataBlockType(t: BlockType): boolean {
  return DATA_BLOCK_TYPES.includes(t);
}

/**
 * Whether a page plan should AI-write its body copy. Only when there's a free-text
 * descriptor AND the page has NO data block — a data page (e.g. "an Offices page")
 * is its own content (the offices block), so we add that deterministically instead
 * of generating prose about it.
 */
export function pagePlanWantsAiCopy(cmd: PagePlanCommand): boolean {
  return !!cmd.descriptor && !cmd.blockTypes.some(isDataBlockType);
}

// ── whole-site scaffold ("make me a website for [Firm]") ───────────────────────
export interface ScaffoldSiteCommand {
  kind: 'scaffold-site';
  firmName: string; // '' when no concrete firm name was given
  vibe: string | null; // optional style hint (luxury / modern / coastal / …)
  replace: boolean; // user explicitly OK'd replacing an existing site ("rebuild", "start fresh", …)
  // Bespoke look parsed straight from the prompt so a from-scratch build reflects the
  // words the agent used — a real palette + font pairing on top of the vibe look, so it
  // isn't just 1-of-5 fixed templates. All optional; absent → the look's own defaults.
  colors?: { primary?: string; accent?: string; background?: string };
  fonts?: { heading?: string; body?: string };
  // Which brand to apply (the guided interview resolves this per-site so it never stamps one site
  // with another's brand): a brand id = apply that brand; `null` = start fresh (no existing brand —
  // the parsed/answered palette drives the look); `undefined` = default (the site's own brand, else
  // the tenant's active brand).
  brandId?: string | null;
  // Guided-interview design choices — applied to `section_looks` (the same store /looks + /cards use).
  // `homeLook` overrides the vibe-derived home look; `cards` sets the site-wide card styles. Absent →
  // the build's own vibe defaults (home) / template defaults (cards).
  homeLook?: string | null;
  cards?: { agentCard?: string; card?: string };
  // A hosted hero photo (Pexels / upload / paste) chosen in the guided interview → written to
  // `style.hero_image_url` so the built home hero uses it (and the header logo contrasts it).
  heroImage?: string;
  // A headline the agent quoted in their opening message and CONFIRMED at the interview's headline
  // step. Stamped onto the home hero after the look is applied — see the note in handleScaffoldSite
  // about why it has to be last.
  heroHeading?: string;
  // Set only by the guided interview. Marks a command as fully answered, so a rebuild confirmation
  // replays it straight away instead of asking the same questions a second time. Without the marker
  // the only test for "do we know enough" is a name AND a vibe, which the interview's own result can
  // fail — an agent who skipped the style question would be interviewed again forever.
  fromInterview?: boolean;
  // The message this command was parsed from, kept for when the build is deferred behind the
  // "say rebuild my site" guard. The confirmation is four words with nothing in them, so an
  // interview seeded from IT would re-ask for the firm name, the market and the quoted headline the
  // agent had already supplied in the message the guard interrupted.
  sourcePrompt?: string;
}

// Descriptive palette words → hex + role. Lets "warm sand, a deep ink-navy, a touch of
// brass" become background/primary/accent (the named-color list misses these phrases).
// Dark tones → primary (brand), metal/warm/vivid → accent, light → background.
type ScaffoldColorRole = 'primary' | 'accent' | 'background';
const SCAFFOLD_PALETTE_WORDS: { re: RegExp; hex: string; role: ScaffoldColorRole }[] = [
  { re: /\b(?:ink[-\s]?navy|deep\s+navy|midnight\s+blue|navy)\b/i, hex: '#1B2A4A', role: 'primary' },
  { re: /\b(?:charcoal|graphite|near[-\s]?black|onyx|slate)\b/i, hex: '#23262B', role: 'primary' },
  { re: /\b(?:forest|hunter\s+green|pine|emerald)\b/i, hex: '#264734', role: 'primary' },
  { re: /\b(?:burgundy|maroon|oxblood|wine)\b/i, hex: '#5C2A2E', role: 'primary' },
  { re: /\b(?:plum|aubergine|eggplant)\b/i, hex: '#3E2148', role: 'primary' },
  { re: /\b(?:brass|bronze|gold(?:en)?)\b/i, hex: '#B08D57', role: 'accent' },
  { re: /\b(?:terracotta|rust|clay)\b/i, hex: '#B5623C', role: 'accent' },
  { re: /\b(?:teal|seafoam)\b/i, hex: '#3E7A7E', role: 'accent' },
  { re: /\b(?:copper)\b/i, hex: '#A65E3B', role: 'accent' },
  { re: /\b(?:blush|dusty\s+rose)\b/i, hex: '#C98A8A', role: 'accent' },
  { re: /\b(?:warm\s+sand|sand|sandy|bone|oat(?:meal)?|greige)\b/i, hex: '#E7DECB', role: 'background' },
  { re: /\b(?:cream|ivory|linen|alabaster|off[-\s]?white)\b/i, hex: '#F5F0E6', role: 'background' },
  { re: /\b(?:sage|eucalyptus)\b/i, hex: '#DDE4D8', role: 'background' },
];

/** Parse a bespoke palette from a build prompt (primary/accent/background). */
export function extractScaffoldColors(prompt: string): ScaffoldSiteCommand['colors'] | undefined {
  const out: { primary?: string; accent?: string; background?: string } = {};
  for (const { re, hex, role } of SCAFFOLD_PALETTE_WORDS) {
    if (!out[role] && re.test(prompt)) out[role] = hex;
  }
  const named = extractColor(prompt); // a literal hex / basic name the phrases missed
  if (named && !Object.values(out).includes(named)) {
    if (!out.primary) out.primary = named;
    else if (!out.accent) out.accent = named;
  }
  return Object.keys(out).length ? out : undefined;
}

// Vibe → look, matched in PRIORITY order (not first-in-string): core market/look
// vibes are checked before descriptor modifiers, so "breezy high-end coastal" → coastal
// and "upscale, modern" → modern. Keys are normalized (no spaces/hyphens) to match the
// scaffoldTemplate look maps.
const VIBE_MATCHERS: [string, RegExp][] = [
  ['coastal', /\bcoastal\b/i],
  ['luxury', /\bluxur(?:y|ious)\b/i],
  ['modern', /\bmodern\b/i],
  ['bold', /\bbold\b/i],
  ['classic', /\bclassic\b/i],
  ['elegant', /\belegant\b/i],
  ['minimalist', /\bminimal(?:ist)?\b/i],
  ['sleek', /\bsleek\b/i],
  ['dark', /\bdark\b/i],
  ['warm', /\bwarm\b/i],
  ['bright', /\bbright\b/i],
  ['highend', /\bhigh[-\s]?end\b/i],
  ['upscale', /\bupscale\b/i],
  ['sophisticated', /\bsophisticated\b/i],
  ['refined', /\brefined\b/i],
  ['editorial', /\beditorial\b/i],
  ['contemporary', /\bcontemporary\b/i],
  ['beachy', /\bbeachy\b/i],
  ['breezy', /\bbreezy\b/i],
  ['airy', /\bairy\b/i],
  ['earthy', /\bearthy\b/i],
  ['rustic', /\brustic\b/i],
  ['natural', /\bnatural\b/i],
  ['homestead', /\bhomestead\b/i],
  ['vibrant', /\bvibrant\b/i],
  ['energetic', /\benergetic\b/i],
  ['traditional', /\btraditional\b/i],
  ['timeless', /\btimeless\b/i],
  ['heritage', /\bheritage\b/i],
];

/** The highest-priority vibe present in the prompt, or null. */
export function matchVibe(prompt: string): string | null {
  for (const [key, re] of VIBE_MATCHERS) {
    if (re.test(prompt)) return key;
  }
  return null;
}

/** Parse a font pairing hint (serif vs geometric sans) into concrete Google fonts. */
export function extractScaffoldFonts(prompt: string): ScaffoldSiteCommand['fonts'] | undefined {
  const p = prompt.toLowerCase();
  if (/\b(?:serif|editorial|elegant|timeless|refined|sophisticated|classic)\b/.test(p)) {
    return { heading: 'Playfair Display', body: 'Inter' };
  }
  if (/\b(?:sans[-\s]?serif|geometric\s+sans|modern\s+sans|clean\s+sans|modern|geometric)\b/.test(p)) {
    return { heading: 'Poppins', body: 'Inter' };
  }
  return undefined;
}

/** A firm name from a "for X" clause, or '' if X is really a descriptor (article-led
 *  like "a luxury brokerage" / "my real estate business") rather than a name. */
export function cleanFirmName(raw: string): string {
  let s = raw.trim().replace(/^["']+|["']+$/g, '').trim();
  // cut at a clause boundary / trailing instruction
  s = s.split(/\s*[,.;]\s*|\s+(?:with|that|featuring|including|using|and\s+|in\s+a\b|so\s+)/i)[0].trim();
  if (/^(a|an|the|my|our|some|his|her|their)\b/i.test(s)) return ''; // descriptor, not a name
  if (s.length > 80) s = s.slice(0, 80).trim();
  return s;
}

/**
 * True only when the USER'S OWN WORDS authorise wiping an existing site.
 *
 * This is the sole authorisation for a destructive rebuild, and it is deliberately
 * grounded in the literal prompt. The agent path used to take `replace` straight from
 * the model's tool input, which meant the model's *claim* about what the user said
 * could bypass the "you already have a site here" guard in handleScaffoldSite. That is
 * exactly how "no, needs to be a luxury farmhouse" — a correction of the previous
 * hero-image turn — rebuilt a live site: the leading "no" reads to a model as "reject
 * this, start over". See docs/BUG_SCAFFOLD_MISFIRE.md.
 *
 * A model may PROPOSE a replace; only the user can AUTHORISE one.
 */
export function userAuthorisedReplace(prompt: string): boolean {
  return /\b(rebuild|replace|start\s+(?:over|fresh)|from\s+scratch|wipe|overwrite|clear\s+(?:it|everything|the\s+site))\b/i
    .test(prompt);
}

/**
 * "make me a [real estate] website for [Firm]", "build a site for X", "spin up my
 * real-estate website". Returns null for a single-page request ("make a listings
 * page") or a styling tweak ("make this site modern") — only a whole-SITE build
 * with an "a/an/my/our … (web)site" shape fires. The firm name + optional vibe are
 * extracted; both are optional (a bare "make me a website" still scaffolds).
 */
export function parseScaffoldSite(prompt: string): ScaffoldSiteCommand | null {
  const p = prompt.trim();
  // Require a build verb + "a/an/my/our [real estate] website/site/homepage".
  if (
    !/\b(make|(?:re)?build|create|set\s*up|generate|design|give|spin\s*up|whip\s*up)\b[^.?!]*?\b(?:me\s+)?(?:a|an|my|our)\s+(?:[a-z][a-z'&/, -]*?\s+)?(?:web\s?site|site|home\s?page)\b/i.test(p)
  ) {
    return null;
  }
  // A single-PAGE request ("make a listings page") is parsePagePlan's job, not ours. But a whole-SITE
  // build that merely MENTIONS a page as a feature ("build a site for X that has … a page for our
  // agents") is still a site build — so only bail on "page" when the request never says site/website.
  if (!/\b(?:web\s?site|site)\b/i.test(p)) {
    const pForPageGuard = p.replace(/\bhome\s?page\b/gi, ' ');
    if (/\bpage\b/i.test(pForPageGuard)) return null;
  }

  let firmName = '';
  const forM = p.match(/\bfor\s+(.+)$/i);
  if (forM) firmName = cleanFirmName(forM[1]);

  // Vibe → the professional look the scaffold applies. Broadened well past the original
  // handful so descriptive language ("high-end", "editorial", "airy", "earthy",
  // "vibrant", "timeless") maps to a look instead of silently falling to the default.
  // PRIORITY-ordered, not first-in-string: a core market/look vibe (coastal, modern…)
  // wins over a modifier (breezy, high-end…) regardless of word order in the prompt.
  const vibe = matchVibe(p);

  // The user explicitly OK'd overwriting an existing site (so we can skip the
  // "this will replace your site" guard).
  const replace = userAuthorisedReplace(p);

  return { kind: 'scaffold-site', firmName, vibe, replace, colors: extractScaffoldColors(p), fonts: extractScaffoldFonts(p) };
}

/**
 * True for the auto-seeded legal pages (Privacy Policy `/privacy`, Terms of Use `/terms`) that
 * `buildLegalPages` provisions on a blank site. These must NOT count as "real content" when the
 * scaffold decides whether a from-scratch build would overwrite an existing site — otherwise a
 * blank site (which only has these two seeds) refuses to build. A site with a real hero/listings
 * page still trips the guard because those pages aren't legal seeds.
 */
export function isLegalSeedPage(page: { slug?: string | null }): boolean {
  const slug = String(page?.slug ?? '').replace(/^\/+|\/+$/g, '').toLowerCase();
  return slug === 'privacy' || slug === 'privacy-policy' || slug === 'terms' || slug === 'terms-of-use';
}

/**
 * A location/market from a build prompt ("… in Palm Beach, FL", "based in Boca Raton", "serving
 * the Hamptons"). Returns '' when none is confidently found: the place must be Capitalized (a
 * proper noun) so "in a modern style" / "for my business" don't false-match, and a bare vibe word
 * ("in Modern") is rejected.
 */
export function extractMarket(prompt: string): string {
  const m = prompt.trim().match(
    /\b(?:based\s+in|located\s+in|serving|around|near|in)\s+((?:the\s+)?[A-Z][A-Za-z.'-]*(?:[\s-][A-Z][A-Za-z.'-]*){0,3}(?:,\s*[A-Za-z]{2}\b)?)/,
  );
  if (!m) return '';
  let place = m[1].trim().replace(/[.,;]+$/, '').trim();
  if (/^(?:a|an|my|our)\b/i.test(place)) return ''; // descriptor, not a place ("the …" is allowed)
  if (matchVibe(place)) return ''; // guard against "in Modern" / "in Coastal"
  return place.length > 60 ? place.slice(0, 60).trim() : place;
}

/** Everything a from-scratch build prompt already tells us, so the interview can pre-fill and ask
 *  ONLY the gaps. Composes the existing scaffold parsers + the new market extractor; strips a
 *  trailing market from the firm name ("John in Miami" → name "John", market "Miami"). Pure. */
export interface BuildFacts {
  firmName: string;
  market: string;
  vibe: string | null;
  colors?: ScaffoldSiteCommand['colors'];
  fonts?: ScaffoldSiteCommand['fonts'];
}
export function extractBuildFacts(prompt: string): BuildFacts {
  const cmd = parseScaffoldSite(prompt);
  const market = extractMarket(prompt);
  let firmName = cmd?.firmName ?? '';
  // Fallback for phrasings cleanFirmName bails on (its "for X" clause led with an article, e.g.
  // "for a real estate broker called SLI Real Estate"): pull the Capitalized name after called/named.
  if (!firmName) {
    const m = prompt.match(/\b(?:called|named|business\s+name\s+is|company\s+is)\s+([A-Z][\w&'.-]*(?:\s+[A-Z0-9][\w&'.-]*){0,5})/);
    if (m) firmName = m[1].trim();
  }
  // Fallback 2: a brokerage-style proper-noun name stated WITHOUT a "for X" / "called X" lead-in
  // ("build me a coastal site, BLT Realty, with search and agents"). The real-estate suffix
  // (Realty / Real Estate / Properties / Group / …) anchors it, so it captures the company without
  // grabbing arbitrary Capitalized words — this is the "I told it the name but it still asked" gap.
  if (!firmName) {
    const m = prompt.match(
      /\b([A-Z][A-Za-z0-9&'.-]*(?:\s+(?:[A-Z0-9][A-Za-z0-9&'.-]*|and|of|the|&)){0,4}\s+(?:Realty|Realtors?|Real\s+Estate|Properties|Property|Group|Homes|Associates|Partners|Advisors|Collective|Brokerage|Agency))\b/,
    );
    if (m) firmName = m[1].replace(/\s+/g, ' ').trim();
  }
  if (market) {
    // Strip a trailing "… in <market>" / "… in <city>" the firm-name parser swallowed
    // ("Harbor Point Realty in Palm Beach" → "Harbor Point Realty").
    const lower = firmName.toLowerCase();
    for (const tail of [market, market.split(',')[0].trim()]) {
      const needle = ` in ${tail.toLowerCase()}`;
      const idx = lower.lastIndexOf(needle);
      if (idx >= 0 && idx + needle.length === lower.length) {
        firmName = firmName.slice(0, idx).trim();
        break;
      }
    }
  }
  return { firmName, market, vibe: cmd?.vibe ?? matchVibe(prompt), colors: cmd?.colors, fonts: cmd?.fonts };
}

/** A slug not already used by `existingSlugs` (appends -2, -3, … if needed). Pure. */
export function uniqueSlug(base: string, existingSlugs: string[]): string {
  const norm = (s: string) => s.replace(/^\/+/, '').replace(/\/+$/, '').toLowerCase();
  const taken = new Set(existingSlugs.map(norm).filter(Boolean));
  const b = norm(base) || 'page';
  if (!taken.has(b)) return b;
  for (let i = 2; i < 1000; i++) {
    const cand = `${b}-${i}`;
    if (!taken.has(cand)) return cand;
  }
  return `${b}-${crypto.randomUUID().slice(0, 8)}`;
}

/**
 * Append a link to the FIRST block of `blockType` (nav or footer — both carry a
 * `links` array) in `blocks`, immutably. Returns the new array and whether such a
 * block was found. Skips an exact-href duplicate so re-running doesn't pile up
 * identical links. Pure — shared by the studio's one-shot page plan.
 */
export function withLinkAppended(
  blocks: Block[],
  link: NavLink,
  blockType: 'nav' | 'footer' = 'nav',
): { blocks: Block[]; linked: boolean } {
  let linked = false;
  const next = blocks.map((b) => {
    if (b.type !== blockType || linked) return b;
    linked = true;
    const existing = (b.props as { links?: NavLink[] }).links ?? [];
    if (existing.some((l) => l.href === link.href)) return b; // already there
    return { ...b, props: { ...b.props, links: [...existing, link] } } as Block;
  });
  return { blocks: next, linked };
}

/** Append a link to the first nav block (immutable). */
export function withNavLinkAppended(blocks: Block[], link: NavLink): { blocks: Block[]; linked: boolean } {
  return withLinkAppended(blocks, link, 'nav');
}

/** Append a link to the first footer block (immutable). */
export function withFooterLinkAppended(blocks: Block[], link: NavLink): { blocks: Block[]; linked: boolean } {
  return withLinkAppended(blocks, link, 'footer');
}

export interface BlockFieldEdit {
  blockType: 'footer';
  // Contact fields (email/phone/address), the footer's identity line (companyName — the business
  // name the footer displays; a materialized footer is otherwise seeded from sites.display_name,
  // which is how a live site ended up signed "My Brand"), + the two color props the footer block
  // supports (backgroundColor / textColor). All take the SAME site-wide update path — the live
  // footer reads the HOME page's footer block, so a per-page write "saves" but never ships.
  field: 'email' | 'phone' | 'address' | 'companyName' | 'backgroundColor' | 'textColor';
  value: string; // '' when the user named the field but gave no value (→ ask)
  /** Phase A — set true only when the change came from the armed confirm chip, so the
   *  handler applies the email change instead of asking to confirm it again. */
  confirmed?: boolean;
}

/**
 * Deterministic parser for "change the footer email/phone/address to X". The footer
 * is a site-wide block (often only on the home page), so this is handled at the chat
 * level — NOT via the current-page block resolver, which is why asking to edit the
 * footer from another page used to fail with "there's no footer on this page". No AI
 * (a structured field set — per the builder AI-cost rules). Returns null if it isn't
 * a footer-field instruction; returns value:'' when the field is named but no value
 * was given (the caller then asks for it).
 */
export function parseBlockFieldEdit(prompt: string): BlockFieldEdit | null {
  const p = prompt.trim();
  const lower = p.toLowerCase();
  if (!/\bfooter\b/.test(lower)) return null;
  if (!/\b(change|set|update|make|edit|replace|use|put)\b/.test(lower)) return null;

  let field: BlockFieldEdit['field'] | null = null;
  // "company name" first — it's the most specific phrase, and "change the footer company name"
  // must not fall through to the bare-noun checks below.
  if (/\b(?:company|business|brand)\s+name\b/.test(lower)) field = 'companyName';
  else if (/\bemail\b/.test(lower)) field = 'email';
  else if (/\b(phone|tel|call|number)\b/.test(lower)) field = 'phone';
  else if (/\baddress\b/.test(lower)) field = 'address';
  if (!field) return null;

  let value = '';
  if (field === 'email') {
    // Local part allows an apostrophe (o'brien@…) — RFC-valid and common in agent
    // names; the old class dropped it, silently truncating "o'brien@…" to "brien@…".
    const m = p.match(/[A-Z0-9._%+'-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    if (m) value = m[0];
  } else if (field === 'phone') {
    const m = p.match(/\+?\d[\d\s().-]{6,}\d/);
    if (m) value = m[0].trim();
  } else if (field === 'companyName') {
    // "my/our company name" and the quoted-brand chip phrasing name NO value — the real name
    // lives in Brand Manager, so those belong to parseBrandNameToFooter (the brand picker).
    // Without this step-aside, "put my company name in the footer" once parsed here with the
    // captured "value" being the literal text "in the footer".
    if (/\b(?:my|our)\s+(?:company|business|brand)\s+name\b/i.test(p)) return null;
    if (/["“'][^"”']+["”']\s+brand\s+name\b/i.test(p)) return null;
    // The footer-locality phrase is never part of the name — strip it wherever it sits, then
    // read the value. Also tolerates the "to say X" phrasing.
    const stripped = p.replace(/\s+(?:in|on|into|at)\s+the\s+footer\b/gi, '');
    const m = stripped.match(/(?:company|business|brand)\s+name\s+(?:to|:|=|is|should\s+(?:be|say|read))?\s*(?:say\s+|read\s+)?(.+)$/i);
    if (m) value = m[1].trim().replace(/^["'“”‘’]+|["'“”‘’]+$/g, '').replace(/[.!?]+$/, '').trim();
  } else {
    const m = p.match(/address\s+(?:to|:|=|is)?\s*(.+)$/i);
    if (m) value = m[1].trim().replace(/^["']|["']$/g, '');
  }
  return { blockType: 'footer', field, value };
}

/**
 * Deterministic "make the footer text/background <color>" → a site-wide footer color edit.
 * The footer is a single site-wide block that the live site resolves from the HOME page, so this
 * MUST take the same server-side updateSiteBlockField('footer', …) path the contact fields use —
 * a per-page block write "saves" but never ships. No AI (a structured color set, per the builder
 * AI-cost rules). Conservative: requires the word "footer" + a concrete color, and defers
 * generative asks ("make the footer pop"). Returns null when it isn't a footer color instruction.
 */
export function parseFooterColorEdit(prompt: string): BlockFieldEdit | null {
  const p = prompt.trim();
  const lower = p.toLowerCase();
  if (!/\bfooter\b/.test(lower)) return null;
  // Needs a recolor verb, so a complaint ("the footer text is hard to read") doesn't write.
  if (!/\b(make|set|change|turn|paint|colou?r|update)\b/.test(lower)) return null;
  // Generative/vague → leave to AI (never a literal color set): "make the footer text easier to read".
  if (/\b(more|less|better|easier|nicer|cleaner|pop|readable|legible|contrast)\b/.test(lower)) return null;
  // A footer LINK color has no dedicated prop — leave it to the AI (don't match "link" here).
  if (/\blink\b/.test(lower)) return null;
  const value = extractColor(p, { requireCue: true });
  if (!value) return null;
  // TEXT/font/copy near "footer" → textColor; otherwise the footer background.
  const field: 'backgroundColor' | 'textColor' =
    /\b(text|font|words?|copy|writing|letters?)\b/.test(lower) ? 'textColor' : 'backgroundColor';
  return { blockType: 'footer', field, value };
}

export interface FooterSaysReplace {
  /** The text the user reports seeing in the footer. */
  says: string;
  /** What it should say instead. */
  value: string;
}

/**
 * Deterministic '"in the footer it says X — change that to say Y"'. A field transcript: the
 * exact message `in the footer it says "My Brand" change that to say "Home Comm Realty"` rode
 * the paid agent, which asked WHERE the text was, then declared its footer tool couldn't set
 * the company name — three turns, no edit. The user names the CURRENT text, not the field, so
 * the field is resolved from the text's SHAPE (an email is an email, a phone number is a phone
 * number, and any other free text is the footer's identity line — companyName). Returns null
 * when it isn't a footer says-replacement; link/menu wording is left to the nav handlers.
 */
export function parseFooterSaysReplace(prompt: string): FooterSaysReplace | null {
  const p = prompt.trim();
  const lower = p.toLowerCase();
  if (!/\bfooter\b/.test(lower)) return null;
  // "the footer link says…" is a LINK rename, which has its own (nav) vocabulary — never
  // write a link label into a footer text field.
  if (/\b(link|menu|nav|button)\b/.test(lower)) return null;
  const clean = (s: string) => s.trim().replace(/^["'“”‘’]+|["'“”‘’]+$/g, '').replace(/[.!?]+$/, '').trim();
  // Quoted says-text first — unambiguous boundaries: it says "X" … change that to (say) Y.
  let m = p.match(
    /\b(?:says?|reads?|shows?|displays?)\s*[:,]?\s*["“']([^"”']{1,120})["”'][\s\S]*?\b(?:change|replace|update|switch|set|make)\b[\s\S]*?\bto\b(?:\s+(?:say|read|show|be))?\s+(.{1,160}?)[\s.!?]*$/i,
  );
  if (!m) {
    // Unquoted fallback: the says-text runs up to the change-verb.
    m = p.match(
      /\b(?:says?|reads?|shows?|displays?)\s*[:,]?\s*(.{1,120}?)\s*[,—–-]?\s*\b(?:change|replace|update|switch|make|set)\b\s*(?:that|it|this)?\s*\bto\b(?:\s+(?:say|read|show|be))?\s+(.{1,160}?)[\s.!?]*$/i,
    );
  }
  if (!m) return null;
  const says = clean(m[1]);
  const value = clean(m[2]);
  if (!says || !value) return null;
  return { says, value };
}

// Phase A — changing the contact email is a known social-engineering vector (lead/contact
// mail silently redirected to an attacker), so the dispatch CONFIRMS an email change with a
// one-tap chip instead of applying it immediately. The chip sends this armed phrase (it shows
// as the user's bubble, so it must read naturally); the dispatch matches it FIRST and applies.
// It deliberately omits the word "footer" so parseBlockFieldEdit can't re-match it as a fresh
// edit. Phone/address aren't confirmed — only the email (the recipient of contact messages).
const CONFIRM_CONTACT_EMAIL_RE = /^yes,?\s+set (?:the )?contact email to\s+(.+?)[\s.!]*$/i;

/** Build the armed confirm phrase the chip sends for an email change. */
export function confirmContactEmailPrompt(value: string): string {
  return `Yes, set the contact email to ${value}`;
}

/** The armed confirmation a chip sends → apply this email change. Returns the email, else null.
 *  The captured value MUST be a single clean email — a bundled rider ("…to bob@x.com and delete
 *  all pages") is rejected so no malformed value is ever written as the site's contact address
 *  and no trailing imperative can ride in on a confirmed action. */
export function parseConfirmedContactEmail(prompt: string): string | null {
  const m = prompt.trim().match(CONFIRM_CONTACT_EMAIL_RE);
  if (!m) return null;
  const value = m[1].trim();
  return /^[A-Z0-9._%+'-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) ? value : null;
}

// ── Phase D — page lifecycle + section reset (structural / corrective intents) ──
// Deterministic; handlers call the existing page-CRUD endpoints. A page reference is either
// the CURRENT page ("this page") or a NAMED page ("the about page"). The handler resolves it.

export interface PageRef {
  name?: string;
  current?: boolean;
}

const PAGE_REF_PRONOUN = /^(this|current|the|a|an|my)$/i;

/**
 * "show/open/go to the X page" — studio NAVIGATION, deterministic and $0. Field transcript:
 * "show the search page" fell through to the cheap planner, which emitted a make_homepage op
 * and the chat answered "(Skipped making a page your home page — you didn't ask for that.)" —
 * a navigation ask met with a non-sequitur about an op nobody requested. Anchored at both
 * ends: the verb opens the prompt and "page" (plus an optional preview clause) closes it, so
 * "show the About page and rewrite it" still goes to the edit paths.
 */
export function parseShowPage(prompt: string): PageRef | null {
  const p = prompt.trim().replace(/[.!?]+$/, '');
  const m = p.match(
    /^(?:can\s+you\s+|could\s+you\s+|please\s+)?(?:show|open|view|preview|go\s+to|take\s+me\s+to|switch\s+to)\s+(?:me\s+)?(?:the\s+|my\s+|our\s+)?(.+?)\s+page(?:\s+in\s+the\s+preview(?:\s+window|\s+pane)?)?$/i,
  );
  if (!m) return null;
  const name = m[1].trim();
  // A pronoun is not a destination, and a long tail is a brief, not a page name.
  if (!name || name.length > 40 || /^(this|that|it|current)$/i.test(name)) return null;
  return { name };
}

/**
 * A "page name" the `(.+?)\s+page` captures produce that is not a page name at all.
 *
 * Two ways they go wrong, both reported:
 *
 * **A section, not a page.** "remove the agents section from the home page" is a SECTION delete
 * with the page as its LOCATION. The lazy capture still swallows the whole clause and yields
 * "agents section from the home", which fuzzy-matches the home page — so asking to remove one
 * section answered *"I can't delete your home page."* The user asked to tidy a page and was told
 * they cannot delete their site.
 *
 * **A locative clause.** Anything after "from/on/in the …" describes WHERE, not WHAT. Requiring
 * a determiner after the preposition keeps real names that contain one — "Terms of Use",
 * "Homes in Palm Beach" — matching as themselves.
 *
 * Rejecting here is not a dead end: the prompt falls through to the section handlers and the
 * planner, which read it correctly.
 */
const NOT_A_PAGE_NAME = /\b(?:sections?|blocks?)\b|\b(?:from|on|in)\s+(?:the|my|this)\b/i;

/** Is this captured string a usable page name? */
function isPageName(name: string): boolean {
  return Boolean(name) && !PAGE_REF_PRONOUN.test(name) && !NOT_A_PAGE_NAME.test(name);
}

/** "delete this page" / "remove the about page". Null for bulk content clears ("delete everything"). */
export function parseDeletePage(prompt: string): PageRef | null {
  const p = prompt.trim();
  if (!/\b(delete|remove|get\s+rid\s+of|trash)\b/i.test(p) || !/\bpage\b/i.test(p)) return null;
  if (/\b(everything|all|whole|entire)\b/i.test(p)) return null; // bulk content clear, not a page delete
  // "remove the join us page FROM THE MENU" is a nav-link removal, not a page delete — the
  // menu clause names WHERE, and the bare-page fallback below used to claim it as "delete the
  // CURRENT page", answering a menu-tidy request with "I can't delete your home page".
  if (/\b(?:from|off|out\s+of|in)\s+(?:the\s+|my\s+)?(?:menu|nav(?:igation)?|header|drop-?down)\b/i.test(p)) return null;
  if (/\b(?:delete|remove|get\s+rid\s+of|trash)\s+(?:this|the\s+current|current|my)\s+page\b/i.test(p)) {
    return { current: true };
  }
  // A name/topic that FOLLOWS "page" — "delete the page called Financing", "…the page about my
  // old listings". The old parser only understood the name BEFORE "page", so "page called X"
  // matched nothing, fell to the bare-page fallback, and deleted the CURRENT page instead. Capture
  // the target (the handler fuzzy-matches / reports not-found) so we never nuke the wrong page.
  const afterPageM = p.match(/\bpage\s+(?:called|named|titled|about|for|regarding)\s+["']?(.+?)["']?[\s.!]*$/i);
  if (afterPageM) {
    const name = cleanPageName(afterPageM[1]);
    if (isPageName(name)) return { name };
  }
  const m = p.match(/\b(?:delete|remove|get\s+rid\s+of|trash)\s+(?:the\s+|my\s+)?(.+?)\s+page\b/i);
  if (m) {
    const name = cleanPageName(m[1]);
    if (isPageName(name)) return { name };
  }
  if (/\b(?:delete|remove|get\s+rid\s+of|trash)\s+(?:the\s+)?page\b/i.test(p)) return { current: true };
  return null;
}

/** "hide the about page" / "unpublish this page" — the gentle alternative to delete. */
export function parseHidePage(prompt: string): PageRef | null {
  const p = prompt.trim();
  if (!/\b(hide|unpublish|take\s+down)\b/i.test(p) || !/\bpage\b/i.test(p)) return null;
  if (/\b(?:hide|unpublish|take\s+down)\s+(?:this|the\s+current|current|my)\s+page\b/i.test(p)) {
    return { current: true };
  }
  const m = p.match(/\b(?:hide|unpublish|take\s+down)\s+(?:the\s+|my\s+)?(.+?)\s+page\b/i);
  if (m) {
    const name = cleanPageName(m[1]);
    if (isPageName(name)) return { name };
  }
  return null;
}

/** "rename this page to X" / "rename the about page to Services" / "call this page Home". */
export function parseRenamePage(prompt: string): (PageRef & { newName: string }) | null {
  const p = prompt.trim();
  if (!/\bpage\b/i.test(p)) return null;
  if (!/\b(rename|re-?name|call)\b/i.test(p) && !/\bchange\s+the\s+name\s+of\b/i.test(p)) return null;
  const m = p.match(
    /\b(?:rename|re-?name|call|change\s+the\s+name\s+of)\s+(?:the\s+|this\s+|my\s+)?(.+?)\s+page\s+(?:to\s+|as\s+|named\s+|called\s+|:\s*)?["']?(.+?)["']?[\s.!]*$/i,
  );
  if (!m) return null;
  const which = m[1].trim();
  const newName = m[2].trim();
  if (!newName || PAGE_REF_PRONOUN.test(newName)) return null;
  const ref: PageRef = PAGE_REF_PRONOUN.test(which) ? { current: true } : { name: cleanPageName(which) };
  return { ...ref, newName };
}

/** "make this the homepage" / "set the about page as home". */
export function parseMakeHomepage(prompt: string): PageRef | null {
  const p = prompt.trim();
  if (!/\b(make|set|mark|use|turn)\b/i.test(p)) return null;
  // Require a real "home page" reference (not a bare "home", so "make this home blue" is ignored).
  if (!/\bhome\s*page\b|\bhomepage\b|\blanding\s+page\b|\bmain\s+page\b|\bas\s+(?:the\s+|my\s+)?home\b|\bthe\s+home\b/i.test(p)) {
    return null;
  }
  if (/\b(?:make|set|mark|use|turn)\s+(?:this|the\s+current|current)\b/i.test(p)) return { current: true };
  // A page NAMED "Home" that is not currently the homepage — "make the Home page the
  // homepage (again)". The bare-'home' rejection below exists so "make the home page blue"
  // is not read as a promotion, but it also made THIS request unparseable — which was the
  // last exit from a real incident: a planner-invented op made "Join Us" the home page, the
  // delete refusal said to make another page home first, and the page to promote back was
  // named Home. Requires the home-noun TWICE (the named page and the role), so the guard
  // below still rejects every single-mention phrasing.
  if (/\b(?:make|set|turn)\s+(?:the\s+|my\s+)?["'“]?home["'”]?\s+page\s+(?:the\s+|my\s+|back\s+(?:to|as|into)\s+(?:the\s+)?|as\s+(?:the\s+)?|into\s+(?:the\s+)?)(?:home\s*page|homepage)\b/i.test(p)) {
    return { name: 'home' };
  }
  const m =
    p.match(/\b(?:make|set|mark|use|turn)\s+(?:the\s+|my\s+)?(.+?)\s+page\b/i) ||
    p.match(/\b(?:make|set|mark|turn)\s+(?:the\s+|my\s+)?(.+?)\s+(?:the\s+|my\s+|as\s+(?:the\s+)?|into\s+(?:the\s+)?)?(?:home\s*page|homepage|landing\s+page)\b/i);
  if (m) {
    const name = cleanPageName(m[1]);
    if (isPageName(name) && !/^home(\s*page)?$/i.test(name)) return { name };
  }
  return null;
}

/** "reset this section" / "reset the hero" / "restore the section to defaults". Section-only
 *  (page/site/everything resets are other intents); the handler resolves the actual block. */
export function parseResetSection(prompt: string): boolean {
  const p = prompt.trim();
  if (!/\b(reset|restore|revert)\b/i.test(p)) return false;
  if (/\bpage\b|\bsite\b|\beverything\b|\bwhole\b/i.test(p)) return false;
  return /\b(section|block)\b/i.test(p) || detectBlockTypesIn(p).length > 0 || /\bto\s+defaults?\b/i.test(p);
}

// Delete is destructive → confirm via an armed chip (like the bulk + email confirms).
export function confirmDeletePagePrompt(ref: PageRef): string {
  return ref.current ? 'Yes, delete this page' : `Yes, delete the ${ref.name} page`;
}
export function parseConfirmedPageDelete(prompt: string): PageRef | null {
  const p = prompt.trim();
  if (/^yes,?\s+delete this page[\s.!]*$/i.test(p)) return { current: true };
  const m = p.match(/^yes,?\s+delete the\s+(.+?)\s+page[\s.!]*$/i);
  if (m) return { name: cleanPageName(m[1]) };
  return null;
}

// ── Phase E — multi-turn corrections (bounded last-action memory) ──
// These interpret a follow-up against the PREVIOUS action; the handler only acts when there's
// a relevant recent action to act on (else it falls through to normal handling).

/** "duplicate this page" / "copy the about page" / "make a copy of this page". */
export function parseDuplicatePage(prompt: string): PageRef | null {
  const p = prompt.trim();
  if (!/\b(duplicate|clone|copy|make\s+a\s+copy\s+of)\b/i.test(p) || !/\bpage\b/i.test(p)) return null;
  if (/\b(?:duplicate|clone|copy|make\s+a\s+copy\s+of)\s+(?:this|the\s+current|current|my)\s+page\b/i.test(p)) {
    return { current: true };
  }
  const m = p.match(/\b(?:duplicate|clone|copy|make\s+a\s+copy\s+of)\s+(?:the\s+|my\s+)?(.+?)\s+page\b/i);
  if (m) {
    const name = cleanPageName(m[1]);
    if (isPageName(name)) return { name };
  }
  if (/\b(?:duplicate|clone|copy)\s+(?:the\s+)?page\b/i.test(p)) return { current: true };
  return null;
}

/** Corrective relocate: "I meant the about page" / "no, put it on the about page" / "that should
 *  be on the contact page". Returns the TARGET page ref; the handler moves the just-added section
 *  there (move, not re-add). Requires a corrective cue so it can't grab a plain "add … page". */
export function parseMeantPage(prompt: string): PageRef | null {
  const p = prompt.trim();
  if (!/\bpage\b/i.test(p)) return null;
  if (!/\b(i\s+meant|actually|instead|rather|put\s+it|move\s+it|that\s+should\s+be|belongs)\b/i.test(p) && !/^no[,\s]/i.test(p)) {
    return null;
  }
  const m =
    p.match(/\b(?:on|to|onto|in)\s+(?:the\s+|my\s+)?(.+?)\s+page\b/i) ||
    p.match(/\bi\s+meant\s+(?:the\s+|my\s+)?(.+?)\s+page\b/i);
  if (m) {
    const name = cleanPageName(m[1]);
    if (isPageName(name)) return { name };
  }
  return null;
}
